import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import './App.css';
import { supabase } from './lib/supabaseClient';
import { useAuth } from './contexts/AuthContext';
import ReviewPage from './components/ReviewPage';
import ScoringChecklist from './components/ScoringChecklist';
import GameHistory from './components/GameHistory';
import DesignerLeaderboard from './components/DesignerLeaderboard';
import Hyperspeed from './components/Hyperspeed';
import CardNav from './components/CardNav';
import MagicBento from './components/MagicBento';
import FindImageShowcase from './components/FindImageShowcase';
import Footer from './components/Footer';
import NewGameSetupModal from './components/NewGameSetupModal';
import GameTypeSelectorModal from './components/GameTypeSelectorModal';
import GameManagement from './components/GameManagement';
import ProtectedRoute from './components/ProtectedRoute';
import AuthModal from './components/AuthModal';
import ProfilePage from './components/ProfilePage';
import MyGames from './components/MyGames';
import ActiveGameView from './components/ActiveGameView';
import JoinGamePage from './components/JoinGamePage';
import ResetPassword from './components/ResetPassword';
import FindImageGame from './components/FindImageGame';
import Particles from './components/Particles';
import MiniCrtTable from './components/MiniCrtTable';
import CardSwap, { Card } from './components/CardSwap';
import TextRevealButton from './components/TextRevealButton';

// Sample hero content for logged-out visitors: the guest hero mirrors the
// member hero, so the profile card and games table need stand-in data.
// Defined at module scope so the `games` prop stays referentially stable
// (MiniCrtTable restarts its typing animation whenever the array identity changes).
const GUEST_DEMO_PROFILE = { name: 'Your Name' };
const GUEST_DEMO_GAMES = [
  { name: 'Neon Launch Sprint', type: 'CC', players: 8 },
  { name: 'Logo Remix Rally', type: 'FI', players: 12 },
  { name: 'Poster Jam Friday', type: 'CC', players: 6 }
];

function App() {
  const { user, userProfile, loading: authLoading, signOut, isHost, passwordRecoveryPending, clearPasswordRecovery } = useAuth();
  const [forceProceed, setForceProceed] = useState(false);
  const [currentView, setCurrentView] = useState(() => {
    const path = window.location.pathname;
    const joinMatch = path.match(/^\/join\/([A-Z0-9]+)$/i);
    if (joinMatch) return 'join-game';
    const knownPaths = { '/leaderboard': 'leaderboard', '/history': 'history', '/my-games': 'my-games', '/find-image': 'find-image', '/profile': 'profile' };
    return knownPaths[path] || 'main';
  });
  const [previousView, setPreviousView] = useState(null); // Track previous view for back navigation
  const [showScrollIcon, setShowScrollIcon] = useState(true); // Show by default on page load
  const [joinCode, setJoinCode] = useState(null); // For join game page
  const [joinCodeInput, setJoinCodeInput] = useState(''); // Track join code input value
  const [showResetPassword, setShowResetPassword] = useState(
    () => window.location.pathname === '/reset-password'
  ); // Control password reset view
  // Track viewport width so the guest-hero CardSwap can size its real layout box
  // responsively (a fixed 420px box overflows narrow phones and breaks the page).
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  const [viewportHeight, setViewportHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 900
  );
  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
      setViewportHeight(window.innerHeight);
    };
    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  // ===== Hero vertical fit =====
  // In the two-column layout the whole hero has to live between the fixed header
  // and the bottom of the viewport — anything taller pushes the bottom of the
  // join card and the "Teams that play together" card below the fold. Rather
  // than trusting arithmetic about the header/button clearances, measure the box
  // the hero is actually given: .hero-fit-area is stretched to the section's
  // content box (App.css, >1024px), so its height IS the available space.
  // The stacked layout (≤1024px) scrolls, so it opts out.
  // Callback refs, not useRef: App() returns a loading screen while auth
  // resolves, so the hero is not in the DOM on first mount. A useRef + effect
  // keyed on the view would attach its observers to nothing and never re-run,
  // which silently disabled every measurement below. Storing the nodes in state
  // means the effect re-runs the moment the hero actually mounts.
  const [heroFitEl, setHeroFitEl] = useState(null);
  const [heroFitAreaHeight, setHeroFitAreaHeight] = useState(null);
  // These constants mirror the .hyperspeed-animation-container padding in
  // App.css and are only the pre-measurement fallback for the first paint.
  const HERO_TOP_CLEARANCE = 116;
  const HERO_BOTTOM_CLEARANCE = 80;
  const heroIsTwoColumn = viewportWidth > 1024;
  const heroAvailableHeight = Math.max(
    320,
    heroIsTwoColumn && heroFitAreaHeight
      ? heroFitAreaHeight
      : viewportHeight - HERO_TOP_CLEARANCE - HERO_BOTTOM_CLEARANCE
  );
  // Compaction factor for the hero's type scale, gaps and paddings: 1 when there
  // is ~720px of usable height, tapering to 0.62 on a short laptop window
  // (13" MacBook ≈ 530px usable) so the cards still end above the fold.
  const heroFitScale = heroIsTwoColumn
    ? Math.min(1, Math.max(0.62, (heroAvailableHeight - 180) / 540))
    : 1;
  const heroScaledRem = (rem, minRem) =>
    `${Math.max(minRem, Math.round(rem * heroFitScale * 100) / 100)}rem`;
  const heroAvatarSize = Math.round(Math.max(52, 72 * heroFitScale));
  const heroJoinInputSize = Math.round(Math.max(20, 26 * heroFitScale));

  // Measure the headline/subtext/button block so the rotating cards above it can
  // claim exactly the height that is left over instead of a guessed constant.
  const [heroBodyEl, setHeroBodyEl] = useState(null);
  const [heroBodyHeight, setHeroBodyHeight] = useState(null);
  // The hero's own laid-out height, so we can tell whether it still overflows the
  // space measured above (see heroFitTransform below).
  const [heroShellEl, setHeroShellEl] = useState(null);
  const [heroNaturalHeight, setHeroNaturalHeight] = useState(null);
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return undefined;
    const targets = [
      [heroBodyEl, setHeroBodyHeight],
      [heroFitEl, setHeroFitAreaHeight],
      // offsetHeight is the pre-transform layout height, so reading it here is
      // not affected by the scale we apply from it — no measurement loop.
      [heroShellEl, setHeroNaturalHeight]
    ].filter(([el]) => el);
    const observers = targets.map(([el, set]) => {
      const measure = () => set(el.offsetHeight);
      measure();
      const observer = new ResizeObserver(measure);
      observer.observe(el);
      return observer;
    });
    return () => observers.forEach((o) => o.disconnect());
  }, [heroBodyEl, heroFitEl, heroShellEl]);

  // Guest-hero CardSwap dimensions: shrink the actual element on small screens so
  // it fits inside the viewport instead of forcing horizontal overflow on mobile.
  // The cards now sit inside the hero card, so leave room for the stack offsets.
  const heroCardWidthByViewport = Math.min(400, Math.max(220, viewportWidth - 96));
  // The card window is ~0.96 × its width tall (card height + the two vertical
  // stack offsets), so convert the leftover height back into a card width.
  const heroCardStackRatio = 300 / 420 + 2 * (52 / 420);
  const heroMeasuredBody = heroBodyHeight != null
    ? heroBodyHeight
    // Pre-measurement fallback (also covers browsers without ResizeObserver).
    : Math.round(340 * heroFitScale);
  const heroCardWindowBudget = heroAvailableHeight
    - heroMeasuredBody
    - parseFloat(heroScaledRem(1.25, 0.7)) * 16 // .hero-right-stack gap
    - parseFloat(heroScaledRem(2, 1.1)) * 16    // .hero-right-stack bottom padding
    - 2                                         // card border
    - 8;                                        // cushion: the rem→px conversions
                                                // above are approximations, so
                                                // stop short of the fold instead
                                                // of landing exactly on it
  const heroCardWidthByHeight = Math.max(190, Math.round(heroCardWindowBudget / heroCardStackRatio));
  // The stacked layout scrolls, but a 386px-tall card stack still pushes the
  // cards below it a long way down the page on a short window, so keep the
  // rotating-card block proportional to the screen height there as well.
  const heroCardWidthByStackedHeight = Math.max(
    190,
    Math.round((viewportHeight * 0.32) / heroCardStackRatio)
  );
  const heroCardWidth = heroIsTwoColumn
    ? Math.min(heroCardWidthByViewport, heroCardWidthByHeight)
    : Math.min(heroCardWidthByViewport, heroCardWidthByStackedHeight);
  const heroCardHeight = Math.round(heroCardWidth * (300 / 420));
  const heroCardDistance = Math.round(48 * (heroCardWidth / 420));
  const heroCardVertical = Math.round(52 * (heroCardWidth / 420));
  // Last line of defence. The compaction above is content-aware but it can only
  // shrink what it knows about (type scale, gaps, the card window) — the left
  // column's cards are as tall as their own content, so on a short window the
  // hero can still end up taller than the measured space. Scale the whole hero
  // down by whatever is left over: the bottom of the join card and of the
  // "Teams that play together" card then always land inside the viewport,
  // shrunk rather than clipped or pushed below the fold.
  const heroFitTransform = (() => {
    if (!heroIsTwoColumn || !heroNaturalHeight || !heroFitAreaHeight) return 1;
    if (heroNaturalHeight <= heroFitAreaHeight) return 1;
    // Quantised so sub-pixel measurement noise can't cause a re-render loop.
    return Math.max(0.6, Math.floor((heroFitAreaHeight / heroNaturalHeight) * 100) / 100);
  })();
  const findImageBackRef = useRef(null); // Ref for Find Image back navigation
  const [findImageJoinCode, setFindImageJoinCode] = useState(null); // Join code for Find Image game
  const [findImageSelectedGameId, setFindImageSelectedGameId] = useState(null); // Game selected from My Games → open that game
  // Memoize the heavy Hyperspeed component so it doesn't re-render on every keystroke
  const memoizedHyperspeed = useMemo(() => (
    <Hyperspeed 
      effectOptions={{
        distortion: 'turbulentDistortion',
        length: 400,
        roadWidth: 10,
        islandWidth: 2,
        lanesPerRoad: 4,
        fovSpeedUp: 150,
        speedUp: 0.5,
        fov: 70,
        carLightsFade: 0.4,
        totalSideLightSticks: 20,
        lightPairsPerRoadWay: 40,
        shoulderLinesWidthPercentage: 0.05,
        brokenLinesWidthPercentage: 0.1,
        brokenLinesLengthPercentage: 0.5,
        lightStickWidth: [0.12, 0.5],
        lightStickHeight: [1.3, 1.7],
        movingAwaySpeed: [15, 20],
        movingCloserSpeed: [-30, -40],
        carLightsLength: [400 * 0.03, 400 * 0.2],
        carLightsRadius: [0.05, 0.14],
        carWidthPercentage: [0.3, 0.5],
        carShiftX: [-0.8, 0.8],
        carFloorSeparation: [0, 5],
        colors: {
          roadColor: 0x080808,
          islandColor: 0x0a0a0a,
          background: 0x000000,
          shoulderLines: 0xffffff,
          brokenLines: 0xffffff,
          leftCars: [0xd856bf, 0x6750a2, 0xc247ac],
          rightCars: [0x03b3c3, 0x0e5ea5, 0x324555],
          sticks: 0x03b3c3
        }
      }}
    />
  ), []);

  // Memoize Particles so it doesn't re-render on state changes
  const memoizedParticles = useMemo(() => (
    <div className="particles-bg-layer">
      <Particles
        particleColors={['#ffffff']}
        particleCount={1000}
        particleSpread={20}
        speed={0.1}
        particleBaseSize={100}
        moveParticlesOnHover
        alphaParticles={false}
        disableRotation={false}
        pixelRatio={1}
      />
    </div>
  ), []);

  const startNewGameClickRef = useRef(null);

  // Handle bento card clicks (e.g., "find-image" mini-game, "play-creative-coaster")
  const handleBentoCardClick = useCallback((action) => {
    if (action === 'find-image') {
      setPreviousView(currentView);
      setCurrentView('find-image');
    } else if (action === 'play-creative-coaster') {
      startNewGameClickRef.current?.(null);
    }
  }, [currentView]);

  // Memoize the MagicBento component so it doesn't re-render on every keystroke
  const memoizedMagicBento = useMemo(() => (
    <MagicBento 
      textAutoHide={true}
      enableStars={true}
      enableSpotlight={true}
      enableBorderGlow={true}
      enableTilt={true}
      enableMagnetism={true}
      clickEffect={true}
      spotlightRadius={300}
      particleCount={12}
      glowColor="132, 0, 255"
      onCardClick={handleBentoCardClick}
    />
  ), [handleBentoCardClick]);

  // Memoize the FindImageShowcase component
  const memoizedFindImageShowcase = useMemo(() => (
    <FindImageShowcase 
      onPlayClick={() => handleBentoCardClick('find-image')}
    />
  ), [handleBentoCardClick]);

  // Memoize the onChange handler to avoid creating a new function on every render
  const handleJoinCodeChange = useCallback((e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    setJoinCodeInput(value);
  }, []);

  // URL <-> view sync map
  const viewToPath = {
    'leaderboard': '/leaderboard',
    'history': '/history',
    'my-games': '/my-games',
    'find-image': '/find-image',
    'profile': '/profile'
  };
  const pathToView = Object.fromEntries(
    Object.entries(viewToPath).map(([v, p]) => [p, v])
  );

  // Check URL on mount and popstate
  useEffect(() => {
    const checkPath = () => {
      const pathname = window.location.pathname;
      const joinMatch = pathname.match(/^\/join\/([A-Z0-9]+)$/i);
      if (pathname === '/reset-password') {
        setShowResetPassword(true);
        return;
      }
      if (joinMatch) {
        setJoinCode(joinMatch[1].toUpperCase());
        setCurrentView('join-game');
      } else if (pathToView[pathname]) {
        setCurrentView(pathToView[pathname]);
      } else if (pathname === '/' || pathname === '') {
        setCurrentView('main');
      }
    };
    
    checkPath();
    window.addEventListener('popstate', checkPath);
    return () => window.removeEventListener('popstate', checkPath);
  }, []);

  // Push URL when view changes (never overwrite an in-progress password reset URL)
  useEffect(() => {
    if (showResetPassword || passwordRecoveryPending || window.location.pathname === '/reset-password') {
      return;
    }
    const targetPath = viewToPath[currentView] || '/';
    if (window.location.pathname !== targetPath) {
      window.history.pushState({}, '', targetPath);
    }
  }, [currentView, showResetPassword, passwordRecoveryPending]);

  // Password recovery: hash tokens, /reset-password path, or AuthContext PASSWORD_RECOVERY event
  useEffect(() => {
    const handlePasswordReset = async () => {
      const hash = window.location.hash;
      const search = window.location.search;
      const hashParams = hash ? new URLSearchParams(hash.substring(1)) : null;
      const queryParams = search ? new URLSearchParams(search) : null;
      const type = hashParams?.get('type') || queryParams?.get('type');
      const hasRecoveryHint =
        type === 'recovery' ||
        window.location.pathname === '/reset-password' ||
        !!hashParams?.get('access_token') ||
        !!queryParams?.get('code');

      if (!hasRecoveryHint && !passwordRecoveryPending) return;

      if (passwordRecoveryPending || type === 'recovery') {
        console.log('[PASSWORD-RESET] Recovery flow detected');
        setShowResetPassword(true);
        if (window.location.pathname !== '/reset-password') {
          window.history.replaceState({}, '', '/reset-password');
        }
        return;
      }

      // Give Supabase a moment to exchange tokens / establish the recovery session
      setTimeout(async () => {
        try {
          const { data: { session }, error } = await supabase.auth.getSession();
          if (error) {
            console.error('[PASSWORD-RESET] Error getting session:', error);
            return;
          }
          if (session) {
            console.log('[PASSWORD-RESET] Session established, ready for password update');
            setShowResetPassword(true);
            if (window.location.pathname !== '/reset-password') {
              window.history.replaceState({}, '', '/reset-password');
            }
          }
        } catch (err) {
          console.error('[PASSWORD-RESET] Error handling reset:', err);
        }
      }, 400);
    };

    handlePasswordReset();
  }, [passwordRecoveryPending]);

  // Restore game from localStorage on mount (for designers)
  useEffect(() => {
    const restoreGame = async () => {
      if (!user || authLoading) return;
      
      // Check if there's a stored game ID
      const storedGameId = localStorage.getItem('currentGameId');
      if (!storedGameId) return;
      
      try {
        // Check if user is a participant in this game
        const { data: participant, error: participantError } = await supabase
          .from('game_participants')
          .select('game_id')
          .eq('game_id', storedGameId)
          .eq('user_id', user.id)
          .single();
        
        // If user is a participant or host, load the game
        if (!participantError || participant) {
          const { data: game, error: gameError } = await supabase
            .from('games')
            .select('*')
            .eq('id', storedGameId)
            .single();
          
          if (!gameError && game) {
            setCurrentGame(game);
            setCurrentView('active-game');
            return;
          }
        }
        
        // If game not found or user not participant, clear stored ID
        localStorage.removeItem('currentGameId');
      } catch (error) {
        console.error('Error restoring game:', error);
        localStorage.removeItem('currentGameId');
      }
    };
    
    restoreGame();
  }, [user, authLoading]);
  
  // Force proceed if loading takes too long
  useEffect(() => {
    if (authLoading) {
      const forceTimeout = setTimeout(() => {
        console.warn('App: Force proceeding after loading timeout');
        setForceProceed(true);
      }, 4000); // 4 seconds
      return () => clearTimeout(forceTimeout);
    } else {
      setForceProceed(false);
    }
  }, [authLoading]);

  // Debug logging
  useEffect(() => {
    console.log('App render - authLoading:', authLoading, 'forceProceed:', forceProceed, 'user:', user?.id, 'userProfile:', userProfile?.id);
  }, [authLoading, forceProceed, user, userProfile]);
  const [showGameManagement, setShowGameManagement] = useState(false); // 'main', 'review', 'history', or 'leaderboard'
  const [currentGame, setCurrentGame] = useState(null); // Current active game
  const [viewingHistoricalGame, setViewingHistoricalGame] = useState(false); // Track if viewing a game from history
  const [gameDetails, setGameDetails] = useState({
    id: null,
    instructions: '',
    timing: '',
    scoringRules: '',
    description: ''
  });
  const [resources, setResources] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [sessionBriefs, setSessionBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showHeaderButton, setShowHeaderButton] = useState(false); // Track if button should show in header
  const [showNewGameModal, setShowNewGameModal] = useState(false); // Control new game setup modal
  const [showGameTypeSelectorModal, setShowGameTypeSelectorModal] = useState(false); // Game type picker before setup
  const [showAuthModal, setShowAuthModal] = useState(false); // Control auth modal
  const [authModalMode, setAuthModalMode] = useState('signin'); // 'signup' or 'signin'
  const [showProfile, setShowProfile] = useState(false); // Control profile page view
  const [pendingGameDetails, setPendingGameDetails] = useState(null); // Store game details before game is created
  const [selectedDesignerFilter, setSelectedDesignerFilter] = useState('All'); // Filter submissions by designer
  const [selectedSessionFilter, setSelectedSessionFilter] = useState('sessionA'); // Filter submissions by session
  const [recentGames, setRecentGames] = useState([]);

  useEffect(() => {
    if (!user) { setRecentGames([]); return; }
    let cancelled = false;
    (async () => {
      try {
        let games = [];
        if (isHost()) {
          const [cc, fi] = await Promise.all([
            supabase.from('games').select('id, game_name, designers, game_status').eq('created_by', user.id).order('created_at', { ascending: false }).limit(4),
            supabase.from('find_image_games').select('id, game_name, game_status').eq('created_by', user.id).order('created_at', { ascending: false }).limit(4)
          ]);
          const ccGames = (cc.data || []).map(g => ({
            name: g.game_name || 'Untitled',
            players: Array.isArray(g.designers) ? g.designers.length : 0,
            type: 'CC',
            status: g.game_status
          }));
          const fiPlayerCounts = {};
          const fiIds = (fi.data || []).map(g => g.id);
          if (fiIds.length > 0) {
            const { data: parts } = await supabase.from('find_image_participants').select('game_id').in('game_id', fiIds);
            (parts || []).forEach(p => { fiPlayerCounts[p.game_id] = (fiPlayerCounts[p.game_id] || 0) + 1; });
          }
          const fiGames = (fi.data || []).map(g => ({
            name: g.game_name || 'Untitled',
            players: fiPlayerCounts[g.id] || 0,
            type: 'FI',
            status: g.game_status
          }));
          games = [...ccGames, ...fiGames].slice(0, 4);
        } else {
          const [cc, fi] = await Promise.all([
            supabase.from('game_participants').select('game_id, games!inner(id, game_name, designers, game_status)').eq('user_id', user.id).order('joined_at', { ascending: false }).limit(4),
            supabase.from('find_image_participants').select('game_id, find_image_games!inner(id, game_name, game_status)').eq('user_id', user.id).order('joined_at', { ascending: false }).limit(4)
          ]);
          const ccGames = (cc.data || []).map(p => ({
            name: p.games?.game_name || 'Untitled',
            players: Array.isArray(p.games?.designers) ? p.games.designers.length : 0,
            type: 'CC',
            status: p.games?.game_status
          }));
          const fiGameIds = (fi.data || []).map(p => p.game_id);
          const fiPlayerCounts = {};
          if (fiGameIds.length > 0) {
            const { data: parts } = await supabase.from('find_image_participants').select('game_id').in('game_id', fiGameIds);
            (parts || []).forEach(p => { fiPlayerCounts[p.game_id] = (fiPlayerCounts[p.game_id] || 0) + 1; });
          }
          const fiGames = (fi.data || []).map(p => ({
            name: p.find_image_games?.game_name || 'Untitled',
            players: fiPlayerCounts[p.game_id] || 0,
            type: 'FI',
            status: p.find_image_games?.game_status
          }));
          games = [...ccGames, ...fiGames].slice(0, 4);
        }
        if (!cancelled) setRecentGames(games);
      } catch (e) {
        console.error('Error fetching recent games:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [user, userProfile]);

  // Reset session when designer changes
  useEffect(() => {
    if (selectedDesignerFilter !== 'All') {
      setSelectedSessionFilter('sessionA'); // Default to sessionA when switching designers
    }
  }, [selectedDesignerFilter]);

  // Check if a game is actively being worked on (has content)
  const isGameActive = async (game) => {
    if (!game) return false;
    
    try {
      // Check if game has submissions
      const { data: submissions } = await supabase
        .from('submissions')
        .select('id')
        .eq('game_id', game.id)
        .limit(1);
      
      if (submissions && submissions.length > 0) return true;
      
      // Check if game has resources
      const { data: resources } = await supabase
        .from('resources')
        .select('id')
        .eq('game_id', game.id)
        .limit(1);
      
      if (resources && resources.length > 0) return true;
      
      // Check if game has details
      const { data: gameDetails } = await supabase
        .from('game_details')
        .select('id')
        .eq('game_id', game.id)
        .limit(1);
      
      if (gameDetails && gameDetails.length > 0) return true;
      
      return false;
    } catch (error) {
      console.error('Error checking if game is active:', error);
      // If we can't check, assume it's not active to be safe
      return false;
    }
  };

  const handleStartNewGameClick = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!user) {
      setAuthModalMode('signin');
      setShowAuthModal(true);
      return;
    }

    if (!isHost()) {
      alert('Only Hosts can create new games. Please contact an administrator.');
      return;
    }

    if (!user.email_confirmed_at) {
      alert('Please verify your email to create a game. Check your inbox for the confirmation link.');
      return;
    }

    // "Play Now" = new game; clear any loaded draft so the form opens empty
    setCurrentGame(null);
    setShowNewGameModal(true);
  };
  startNewGameClickRef.current = handleStartNewGameClick;

  // Load or create current game
  const loadOrCreateCurrentGame = async () => {
    try {
      // Try to find the most recent active game (no end date or most recent)
      const { data: games, error } = await supabase
        .from('games')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) {
        // Check if it's a schema cache error
        if (error.code === 'PGRST205') {
          console.log('Schema cache error detected. This is temporary - the table exists but PostgREST needs to refresh.');
          console.log('Waiting 5 seconds and retrying...');
          
          // Wait longer for cache to refresh
          await new Promise(resolve => setTimeout(resolve, 5000));
          
          const { data: retryGames, error: retryError } = await supabase
            .from('games')
            .select('*')
            .order('created_at', { ascending: false })
            .limit(1);
          
          if (retryError) {
            console.error('Retry also failed:', retryError);
            console.log('The games table exists but PostgREST API cache hasn\'t refreshed yet.');
            console.log('The app will continue to work, but game features will be limited.');
            console.log('Please wait 2-5 minutes and refresh the page.');
            // Set loading to false so the app can still render
            setLoading(false);
            
            // Try to create a game anyway - maybe it will work
            const newGame = await createNewGame('Current Game', null, { silent: true });
            if (newGame) {
              setCurrentGame(newGame);
            }
            return;
          }
          
          if (retryGames && retryGames.length > 0) {
            setCurrentGame(retryGames[0]);
            setLoading(false);
            return;
          }
          
          // If retry worked but no games exist, create one
          const newGame = await createNewGame('Current Game', null, { silent: true });
          if (newGame) {
            setCurrentGame(newGame);
            setLoading(false);
          } else {
            setLoading(false);
          }
          return;
        }
        
        // Non-cache error
        console.error('Error loading current game:', error);
        console.error('This might be a schema cache issue. Waiting 2 seconds and retrying...');
        
        // Wait 2 seconds and retry once (gives cache time to refresh)
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        const { data: retryGames, error: retryError } = await supabase
          .from('games')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(1);
        
        if (retryError) {
          console.error('Retry also failed:', retryError);
          setLoading(false);
          
          // Try to create a game anyway - maybe it will work
          const newGame = await createNewGame('Current Game', null, { silent: true });
          if (newGame) {
            setCurrentGame(newGame);
          }
          return;
        }
        
        if (retryGames && retryGames.length > 0) {
          setCurrentGame(retryGames[0]);
          setLoading(false);
          return;
        }
        
        // If retry worked but no games exist, create one
        const newGame = await createNewGame('Current Game', null, { silent: true });
        if (newGame) {
          setCurrentGame(newGame);
          setLoading(false);
        } else {
          setLoading(false);
        }
        return;
      }

      if (games && games.length > 0) {
        setCurrentGame(games[0]);
        setViewingHistoricalGame(false); // Default game, not from history
        setLoading(false);
      } else {
        // No games exist, create a default one
        const newGame = await createNewGame('Current Game', null, { silent: true });
        if (newGame) {
          setCurrentGame(newGame);
          setLoading(false);
        } else {
          setLoading(false);
        }
      }
    } catch (error) {
      console.error('Error in loadOrCreateCurrentGame:', error);
      // Try to create a default game as fallback
      try {
        const newGame = await createNewGame('Current Game', null, { silent: true });
        if (newGame) {
          setCurrentGame(newGame);
        }
      } catch (createError) {
        console.error('Failed to create default game:', createError);
      } finally {
        setLoading(false);
      }
    }
  };

  // Create a new game
  const createNewGame = async (gameName = null, gameSetupData = null, { silent = false } = {}) => {
    try {
      if (!user) {
        return null;
      }

      // Require hosts to have a confirmed email before creating games
      if (!user.email_confirmed_at) {
        if (!silent) {
          alert('Please verify your email to create a game. Check your inbox for the confirmation link.');
        }
        return null;
      }

      const name = gameName || `Game - ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      
      let data = null;
      let error = null;
      
      // Check if we're updating an existing draft
      if (gameSetupData?.draftGameId) {
        // Update existing draft to finalize it
        const { data: updateData, error: updateError } = await supabase
          .from('games')
          .update({
            game_name: name,
            game_status: 'draft' // Keep as draft until game is actually started
          })
          .eq('id', gameSetupData.draftGameId)
          .select()
          .single();

        if (updateError) {
          console.error('Error updating draft game:', updateError);
          throw updateError;
        }

        if (updateData) {
          // Use the updated game
          data = [updateData];
          error = null;
        }
      } else {
        // Try the insert (new game)
        const gameInsertData = {
          game_name: name,
          game_date: new Date().toISOString(),
          game_status: 'draft'
        };
        
        // Add created_by if user is authenticated
        if (user?.id) {
          gameInsertData.created_by = user.id;
        }
        
        const insertResult = await supabase
          .from('games')
          .insert([gameInsertData])
          .select();
        
        data = insertResult.data;
        error = insertResult.error;
      }
      
      // If it fails with schema cache error, wait and retry
      if (error && error.code === 'PGRST205') {
        console.log('Schema cache error detected. Waiting 3 seconds and retrying...');
        await new Promise(resolve => setTimeout(resolve, 3000));
        
        const retry = await supabase
          .from('games')
          .insert([{
            game_name: name,
            game_date: new Date().toISOString()
          }])
          .select();
        
        data = retry.data;
        error = retry.error;
      }

      if (error) {
        console.error('Error creating new game:', error);
        console.error('Error details:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint
        });
        
        // Don't show alert for schema cache errors - they're temporary
        if (error.code === 'PGRST205') {
          console.log('Schema cache error - this is temporary. The table exists but PostgREST cache needs to refresh.');
          console.log('Waiting 5 seconds and retrying one more time...');
          
          // Wait longer and try once more
          await new Promise(resolve => setTimeout(resolve, 5000));
          
          const finalRetry = await supabase
            .from('games')
            .insert([{
              game_name: name,
              game_date: new Date().toISOString()
            }])
            .select();
          
          if (finalRetry.error) {
            console.error('Final retry also failed:', finalRetry.error);
            console.error('The games table exists but PostgREST API cache hasn\'t refreshed yet.');
            console.error('Please wait 2-5 minutes and refresh the page. The table was created successfully.');
            // Don't show alert - just return null and let the app continue
            return null;
          }
          
          // Success on final retry
          data = finalRetry.data;
          error = null;
        } else {
          // Only show alert for non-cache errors
          alert(`Failed to create new game: ${error.message || 'Please check the console for details.'}`);
          return null;
        }
      }
      
      if (data && data.length > 0) {
        const newGame = data[0];
        // Set currentGame immediately so saveGameDetails can use it
        setCurrentGame(newGame);
        setViewingHistoricalGame(false); // New game, not from history
        console.log('Created new game:', newGame);
        
        // Save critical data immediately (before navigation) so it's available when page loads
        // Save designers
        if (gameSetupData?.designers && gameSetupData.designers.length > 0) {
          try {
            console.log('Saving designers to game:', {
              gameId: newGame.id,
              designersCount: gameSetupData.designers.length,
              designers: gameSetupData.designers
            });
            
            const { error: designersError } = await supabase
              .from('games')
              .update({ designers: gameSetupData.designers })
              .eq('id', newGame.id);
            
            if (designersError) {
              console.error('Error saving designers:', designersError);
            } else {
              console.log('Designers saved successfully:', gameSetupData.designers);
              newGame.designers = gameSetupData.designers;
            }
          } catch (error) {
            console.error('Error saving designers:', error);
          }
        } else {
          console.warn('No designers to save:', gameSetupData?.designers);
        }
        
        // Save sessions immediately (before navigation)
        if (gameSetupData && gameSetupData.sessions && gameSetupData.sessions.length > 0) {
          try {
            console.log('Saving sessions immediately:', gameSetupData.sessions);
            
            // Save session briefs and requirements
            for (const session of gameSetupData.sessions) {
              console.log('Processing session:', session);
              const sessionKey = session.sessionKey || (session === gameSetupData.sessions[0] ? 'sessionA' : 'sessionB');
              
              // Save session brief
              // First try to update existing
              const { data: existingBrief } = await supabase
                .from('session_briefs')
                .select('id')
                .eq('session_name', sessionKey)
                .eq('game_id', newGame.id)
                .single();

              if (existingBrief) {
                const { error: briefError } = await supabase
                  .from('session_briefs')
                  .update({
                    session_display_name: session.name,
                    brief_text: session.creativeBrief,
                    target_audience: session.targetAudience,
                    timer_duration: session.timerDuration || 15,
                    updated_at: new Date().toISOString()
                  })
                  .eq('id', existingBrief.id);

                if (briefError) {
                  console.error('Error updating session brief:', briefError);
                }
              } else {
                const { data: insertedBrief, error: briefError } = await supabase
                  .from('session_briefs')
                  .insert({
                    session_name: sessionKey,
                    game_id: newGame.id,
                    session_display_name: session.name,
                    brief_text: session.creativeBrief,
                    target_audience: session.targetAudience,
                    timer_duration: session.timerDuration || 15,
                    updated_at: new Date().toISOString()
                  })
                  .select();

                if (briefError) {
                  console.error('❌ Error inserting session brief:', briefError);
                  console.error('📋 Error message:', briefError.message);
                  console.error('🔢 Error code:', briefError.code);
                  console.error('📝 Error details:', briefError.details);
                  console.error('💡 Error hint:', briefError.hint);
                  console.error('🔑 Session key:', sessionKey);
                  console.error('🎮 Game ID:', newGame.id);
                  console.error('📊 Session data being inserted:', {
                    session_display_name: session.name,
                    brief_text: session.creativeBrief?.substring(0, 50) + '...',
                    target_audience: session.targetAudience?.substring(0, 50) + '...',
                    timer_duration: session.timerDuration || 15
                  });
                } else {
                  console.log('Successfully inserted session brief:', insertedBrief);
                }
              }

              // Save session requirements
              if (session.requirements && session.requirements.length > 0) {
                // Delete existing requirements for this session/game
                await supabase
                  .from('session_requirements')
                  .delete()
                  .eq('session_name', sessionKey)
                  .eq('game_id', newGame.id);

                // Insert new requirements
                const requirementsToInsert = session.requirements
                  .filter(req => req.trim() !== '')
                  .map(req => ({
                    session_name: sessionKey,
                    game_id: newGame.id,
                    requirement_text: req
                  }));

                if (requirementsToInsert.length > 0) {
                  const { data: insertedReqs, error: reqError } = await supabase
                    .from('session_requirements')
                    .insert(requirementsToInsert)
                    .select();

                  if (reqError) {
                    console.error('Error saving session requirements:', reqError);
                  } else {
                    console.log('Successfully inserted requirements:', insertedReqs);
                  }
                }
              }
            }
            
            console.log('Sessions saved successfully');
          } catch (sessionError) {
            console.error('Error saving sessions:', sessionError);
          }
        }
        
        // Save other details (client changes, bonus requirements, game details) in background
        if (gameSetupData && gameSetupData.sessions) {
          // Run remaining saves in background - don't await
          (async () => {
            try {
              // Save client changes
              if (gameSetupData.clientChanges) {
              for (const sessionChanges of gameSetupData.clientChanges) {
                const sessionKey = sessionChanges.sessionKey;
                
                // Delete existing client changes for this session/game
                await supabase
                  .from('client_changes')
                  .delete()
                  .eq('session_name', sessionKey)
                  .eq('game_id', newGame.id);

                // Insert new client changes
                if (sessionChanges.changes && sessionChanges.changes.length > 0) {
                  const changesToInsert = sessionChanges.changes
                    .filter(change => change.text && change.text.trim() !== '')
                    .map(change => ({
                      session_name: sessionKey,
                      game_id: newGame.id,
                      change_text: change.text,
                      points: change.points || 1,
                      is_bonus: change.isBonus || false
                    }));

                  if (changesToInsert.length > 0) {
                    const { data: insertedChanges, error: changesError } = await supabase
                      .from('client_changes')
                      .insert(changesToInsert)
                      .select();

                    if (changesError) {
                      console.error('❌ Error saving client changes:', changesError);
                      console.error('📋 Error message:', changesError.message);
                      console.error('🔢 Error code:', changesError.code);
                      console.error('📝 Error details:', changesError.details);
                      console.error('💡 Error hint:', changesError.hint);
                      console.error('🔑 Session key:', sessionKey);
                      console.error('🎮 Game ID:', newGame.id);
                      console.error('📊 Changes being inserted:', changesToInsert.map(c => ({
                        text: c.change_text?.substring(0, 30) + '...',
                        points: c.points,
                        is_bonus: c.is_bonus
                      })));
                    } else {
                      console.log('Successfully inserted client changes:', insertedChanges);
                    }
                  }
                }
              }
            }

            // Save game details (instructions, timing, scoring rules, description)
            // Combine session info into game details for backward compatibility
            const instructions = gameSetupData.sessions.map(s => 
              `${s.name}: ${s.creativeBrief}`
            ).join('\n\n');
            
            const description = gameSetupData.sessions.map(s => 
              `Target Audience: ${s.targetAudience}`
            ).join('\n\n');

            console.log('💾 Saving voice timing config:', gameSetupData.voiceTimingConfig);
            // Pass newGame directly to avoid state timing issues
            await saveGameDetails({
              instructions: instructions,
              timing: '', // Can be added later if needed
              scoringRules: `Each client change is worth points. Bonus requirements are worth more points.`,
              description: description,
              voiceTimingConfig: gameSetupData.voiceTimingConfig || null
            }, newGame); // Pass newGame directly
            console.log('✅ Game details saved with voice timing config');

            // Save bonus requirements to client_changes with is_bonus flag
            if (gameSetupData.bonusRequirements) {
              for (const bonusReq of gameSetupData.bonusRequirements) {
                const sessionKey = bonusReq.sessionKey;
                
                if (bonusReq.text && bonusReq.text.trim() !== '') {
                  // Delete existing bonus requirements for this session/game
                  await supabase
                    .from('client_changes')
                    .delete()
                    .eq('session_name', sessionKey)
                    .eq('game_id', newGame.id)
                    .eq('is_bonus', true);

                  // Insert bonus requirement
                  const { data: insertedBonus, error: bonusError } = await supabase
                    .from('client_changes')
                    .insert({
                      session_name: sessionKey,
                      game_id: newGame.id,
                      change_text: bonusReq.text,
                      points: bonusReq.points || 4,
                      is_bonus: true
                    })
                    .select();

                  if (bonusError) {
                    console.error('❌ Error saving bonus requirement:', bonusError);
                    console.error('📋 Error message:', bonusError.message);
                    console.error('🔢 Error code:', bonusError.code);
                    console.error('📝 Error details:', bonusError.details);
                    console.error('💡 Error hint:', bonusError.hint);
                    console.error('🔑 Session key:', sessionKey);
                    console.error('🎮 Game ID:', newGame.id);
                    console.error('📊 Bonus data:', {
                      text: bonusReq.text?.substring(0, 50) + '...',
                      points: bonusReq.points || 4
                    });
                  } else {
                    console.log('Successfully inserted bonus requirement:', insertedBonus);
                  }
                }
              }
            }

            // Designers already saved above, skip here

            } catch (saveError) {
              console.error('Error saving game data in background:', saveError);
              // Don't fail the whole operation, just log the error
            }
          })(); // Execute immediately, don't await
        } else if (gameSetupData && gameSetupData.instructions) {
          // Old structure: Legacy game details format
          // Use newGame directly instead of relying on currentGame state
          try {
            const detailsToSave = {
              instructions: gameSetupData.instructions || '',
              timing: gameSetupData.timing || '',
              scoring_rules: gameSetupData.scoringRules || '',
              description: gameSetupData.description || '',
              game_id: newGame.id,
              updated_at: new Date().toISOString()
            };

            const { data: detailsData, error: detailsError } = await supabase
              .from('game_details')
              .insert([detailsToSave])
              .select()
              .single();

            if (detailsError) {
              console.error('Error saving game details:', detailsError);
              // Don't throw - just log the error, game is already created
            } else if (detailsData) {
              setGameDetails({ ...gameSetupData, id: detailsData.id });
            }
          } catch (detailsError) {
            console.error('Error saving game details:', detailsError);
            // Don't throw - game is already created, just log the error
          }
        }
        
        // Don't block on loading details/resources - let them load when component mounts
        // The GameManagement component will handle loading its own data
        // Load in background without blocking
        setTimeout(() => {
          loadGameDetails();
          loadResources();
        }, 0);
        
        // Return game - all critical data (sessions, designers) should be saved by now
        console.log('createNewGame: Returning game, sessions and designers should be saved');
        return newGame;
      } else {
        console.error('No data returned from game creation');
        return null;
      }
    } catch (error) {
      console.error('Error creating new game:', error);
      console.error('Full error object:', JSON.stringify(error, null, 2));
      alert(`Failed to create new game: ${error.message || 'Please check the console for details.'}`);
      return null;
    }
  };

  // Load game details from Supabase (authenticated users only — guests browse without auto-creating games)
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setLoading(false);
      return;
    }
    loadOrCreateCurrentGame().then(() => {
      // These will be called after currentGame is set
    });
  }, [user, authLoading]);

  // Load game details, resources, and submissions when currentGame changes
  useEffect(() => {
    if (currentGame && user) {
      // Only load resources if user is authenticated
      loadGameDetails();
      loadResources();
      if (viewingHistoricalGame) {
        loadSubmissions();
        loadSessionBriefs();
    }
    }
  }, [currentGame, viewingHistoricalGame, user]);

  // Track scroll position to show/hide header button and scroll icon
  useEffect(() => {
    if (currentView !== 'main') {
      setShowScrollIcon(false);
      return;
    }

    const handleScroll = () => {
      const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      const viewportHeight = window.innerHeight;
      setShowHeaderButton(scrollPosition > viewportHeight * 0.8);
      setShowScrollIcon(scrollPosition < 50);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [currentView]);

  // Option 3: when landing on main, scroll to top so first viewport is in view
  useEffect(() => {
    if (currentView === 'main' && !viewingHistoricalGame) {
      window.scrollTo(0, 0);
    }
  }, [currentView, viewingHistoricalGame]);

  // Clear selected Find Image game when leaving find-image view (avoid stale open-game when coming from main)
  useEffect(() => {
    if (currentView !== 'find-image') {
      setFindImageSelectedGameId(null);
    }
  }, [currentView]);

  const loadGameDetails = async () => {
    if (!currentGame) return;
    
    try {
      const { data, error } = await supabase
        .from('game_details')
        .select('*')
        .eq('game_id', currentGame.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error) {
        // PGRST116 = no rows returned (not an error)
        // 406 = Not Acceptable (RLS or API format issue)
        if (error.code === 'PGRST116') {
          // No game details found - this is fine
        } else if (error.status === 406 || error.message?.includes('406')) {
          console.warn('Game details query returned 406 - this may be a temporary API issue or RLS policy. Skipping game details.');
        } else {
        console.error('Error loading game details:', error);
        }
      }
      
      if (data) {
        setGameDetails({
          id: data.id,
          instructions: data.instructions || '',
          timing: data.timing || '',
          scoringRules: data.scoring_rules || '',
          description: data.description || '',
          voiceTimingConfig: data.voice_timing_config ? JSON.parse(data.voice_timing_config) : null
        });
      } else {
        // No game details for this game, use defaults
        setGameDetails({
          id: null,
          instructions: '',
          timing: '',
          scoringRules: '',
          description: '',
          voiceTimingConfig: null
        });
      }
    } catch (error) {
      console.error('Error loading game details:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadResources = async () => {
    if (!currentGame) {
      console.log('No current game, skipping resource load');
      setResources([]);
      return;
    }
    
    try {
      console.log('Loading resources for game:', currentGame.id);
      const { data, error } = await supabase
        .from('resources')
        .select('*')
        .eq('game_id', currentGame.id)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error loading resources:', error);
        // 406 errors are often RLS or API format issues - log but don't break
        if (error.code === 'PGRST116' || error.message?.includes('406') || error.status === 406) {
          console.warn('Resources query returned 406 - this may be a temporary API issue or RLS policy. Setting empty resources.');
        }
        setResources([]);
        return;
      }
      
      console.log('Resources query result:', { data, error });
      
      if (data) {
        console.log(`Loaded ${data.length} resources for game ${currentGame.id}`);
        const resourcesWithUrls = await Promise.all(data.map(async (res) => {
          // For link resources: url = external link, file_path = image path
          // For file resources: url = image URL, file_path = image path
          
          let externalUrl = res.url; // Keep the original URL (external link for link types)
          let imageUrl = null;
          
          if (res.type === 'link') {
            // For link resources: url is the external link, file_path is for the image
            // Generate image URL from file_path for display
            if (res.file_path) {
              const { data: { publicUrl } } = supabase.storage
                .from('design-submissions')
                .getPublicUrl(res.file_path);
              imageUrl = publicUrl;
            }
            // Keep externalUrl as the external link (don't overwrite it)
          } else {
            // For file resources: url is the image URL
            // If we have file_path, use it to generate the image URL
            if (res.file_path) {
              const { data: { publicUrl } } = supabase.storage
                .from('design-submissions')
                .getPublicUrl(res.file_path);
              imageUrl = publicUrl;
              // Update url field if it's different (for file resources, url should be image URL)
              if (res.url !== publicUrl) {
                try {
                  await supabase
                    .from('resources')
                    .update({ url: publicUrl })
                    .eq('id', res.id);
                  console.log('Updated file resource URL for:', res.name);
                } catch (updateError) {
                  console.error('Error updating resource URL:', updateError);
                }
              }
            } else {
              // Fallback to url if no file_path
              imageUrl = res.url;
            }
          }
          
          return {
            id: res.id,
            name: res.name,
            type: res.type,
            url: externalUrl, // This is the external link for link types, image URL for file types
            file_path: res.file_path,
            imageUrl: imageUrl // Separate field for the image display URL
          };
        }));
        
        setResources(resourcesWithUrls);
      }
    } catch (error) {
      console.error('Error loading resources:', error);
    }
  };

  const loadSubmissions = async () => {
    if (!currentGame) {
      setSubmissions([]);
      return;
    }
    
    try {
      // Load all submissions for this game
      const { data: submissionsData, error: submissionsError } = await supabase
        .from('submissions')
        .select('*')
        .eq('game_id', currentGame.id)
        .order('created_at', { ascending: false });

      if (submissionsError) {
        console.error('Error loading submissions:', submissionsError);
        setSubmissions([]);
        return;
      }

      if (submissionsData && submissionsData.length > 0) {
        // Load scores for each submission
        const submissionIds = submissionsData.map(s => s.id);
        const { data: scoresData, error: scoresError } = await supabase
          .from('scores')
          .select('*')
          .in('submission_id', submissionIds);

        if (scoresError) {
          console.error('Error loading scores:', scoresError);
        }

        // Combine submissions with scores
        const submissionsWithScores = submissionsData.map(submission => {
          const scores = {};
          if (scoresData) {
            scoresData
              .filter(score => score.submission_id === submission.id)
              .forEach(score => {
                scores[score.requirement_id] = score.score;
              });
          }
          
          const totalScore = Object.values(scores).reduce((sum, score) => sum + (parseInt(score) || 0), 0);
          
          return {
            id: submission.id,
            designer_name: submission.designer_name,
            session_name: submission.session_name,
            image_url: submission.image_url,
            scores: scores,
            totalScore: totalScore,
            created_at: submission.created_at
          };
        });

        setSubmissions(submissionsWithScores);
      } else {
        setSubmissions([]);
      }
    } catch (error) {
      console.error('Error loading submissions:', error);
      setSubmissions([]);
    }
  };

  const loadSessionBriefs = async () => {
    if (!currentGame) {
      setSessionBriefs([]);
      return;
    }
    
    try {
      const { data: briefsData, error: briefsError } = await supabase
        .from('session_briefs')
        .select('session_name, session_display_name')
        .eq('game_id', currentGame.id);

      if (briefsError) {
        console.error('Error loading session briefs:', briefsError);
        setSessionBriefs([]);
        return;
      }

      setSessionBriefs(briefsData || []);
    } catch (error) {
      console.error('Error loading session briefs:', error);
      setSessionBriefs([]);
    }
  };

  const saveGameDetails = async (details, gameOverride = null) => {
    const gameToUse = gameOverride || currentGame;
    if (!gameToUse) {
      console.error('Cannot save game details: no current game');
      return;
    }
    
    try {
      const detailsToSave = {
        instructions: details.instructions || '',
        timing: details.timing || '',
        scoring_rules: details.scoringRules || '',
        description: details.description || '',
        voice_timing_config: details.voiceTimingConfig ? JSON.stringify(details.voiceTimingConfig) : null,
        game_id: gameToUse.id,
        updated_at: new Date().toISOString()
      };
      
      console.log('💾 Saving game details:', {
        gameId: gameToUse.id,
        voiceTimingConfig: details.voiceTimingConfig,
        voice_timing_config_string: detailsToSave.voice_timing_config,
        gameDetailsId: gameDetails.id
      });

      // Check if a game_details record already exists for this game_id
      const { data: existingDetails, error: checkError } = await supabase
        .from('game_details')
        .select('id')
        .eq('game_id', gameToUse.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (checkError && checkError.code !== 'PGRST116') {
        console.error('Error checking for existing game details:', checkError);
        throw checkError;
      }

      if (existingDetails?.id || gameDetails.id) {
        // Update existing record
        const idToUpdate = existingDetails?.id || gameDetails.id;
        console.log('📝 Updating existing game_details record:', idToUpdate);
        console.log('📝 Update data:', JSON.stringify(detailsToSave, null, 2));
        const { data: updatedData, error } = await supabase
          .from('game_details')
          .update(detailsToSave)
          .eq('id', idToUpdate)
          .select()
          .single();

        if (error) {
          console.error('❌ Error updating game details:', error);
          console.error('Error details:', {
            message: error.message,
            code: error.code,
            details: error.details,
            hint: error.hint
          });
          throw error;
        }
        console.log('✅ Successfully updated game_details:', updatedData);
        console.log('✅ Voice timing config in DB:', updatedData?.voice_timing_config);
      } else {
        // Create new record
        console.log('➕ Creating new game_details record');
        console.log('➕ Insert data:', JSON.stringify(detailsToSave, null, 2));
        const { data, error } = await supabase
          .from('game_details')
          .insert([detailsToSave])
          .select()
          .single();

        if (error) {
          console.error('❌ Error inserting game details:', error);
          console.error('Error details:', {
            message: error.message,
            code: error.code,
            details: error.details,
            hint: error.hint
          });
          throw error;
        }
        if (data) {
          console.log('✅ Successfully created game_details:', data.id);
          console.log('✅ Voice timing config in DB:', data?.voice_timing_config);
          setGameDetails({ ...details, id: data.id });
        }
      }
    } catch (error) {
      console.error('❌ Error saving game details:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint
      });
      alert('Failed to save game details. Please try again.');
    }
  };

  // Helper function to check if action requires auth
  const requireAuth = (action) => {
    if (!user) {
      setAuthModalMode('signin');
      setShowAuthModal(true);
      return false;
    }
    return true;
  };

  // Helper function to check if action requires host
  const requireHost = (action) => {
    if (!user) {
      setAuthModalMode('signin');
      setShowAuthModal(true);
      return false;
    }
    if (!isHost()) {
      alert('This action requires Host privileges.');
      return false;
    }
    return true;
  };

  // Host actions that create games require a verified email
  const requireVerifiedHost = () => {
    if (!requireHost('create-game')) return false;
    if (!user.email_confirmed_at) {
      alert('Please verify your email to create a game. Check your inbox for the confirmation link.');
      return false;
    }
    return true;
  };

  // Navigation items for CardNav - memoized to prevent unnecessary recreations
  // Must be defined before early returns to satisfy React hooks rules
  const navItems = useMemo(() => [
    {
      label: "Game",
      bgColor: "#0D0716",
      textColor: "#fff",
      links: [
        // Only show Game History to hosts
        ...(isHost() ? [{
          label: "Game History", 
          ariaLabel: "View Game History",
          onClick: (e) => {
            e.preventDefault();
            if (requireAuth('history')) {
            setCurrentView('history');
            }
          }
        }] : []),
        { 
          label: "My Games", 
          ariaLabel: "View My Games",
          onClick: (e) => {
            e.preventDefault();
            if (requireAuth('my-games')) {
              setCurrentView('my-games');
            }
          }
        }
      ]
    },
            {
              label: "Leaderboard",
              bgColor: "#271E37",
              textColor: "#fff",
              links: [
                { 
                  label: "Designer Leaderboard", 
                  ariaLabel: "View Designer Leaderboard",
                  onClick: (e) => {
                    e.preventDefault();
                    if (requireAuth('leaderboard')) {
                      setCurrentView('leaderboard');
                    }
                  }
                }
              ]
            }
  ], [isHost, user, setCurrentGame, setShowNewGameModal, signOut, setCurrentView, setAuthModalMode, setShowAuthModal, requireAuth, requireHost]);

  // Show loading while auth is initializing (but with timeout)
  if (authLoading && !forceProceed) {
    console.log('App: Showing loading screen');
    return (
      <div className="app" style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#060010',
        color: '#ffffff'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  // Helper function to render game summary
  const renderGameSummary = () => {
    if (submissions.length === 0) return null;
    
    const sessionASubmissions = submissions.filter(s => s.session_name === 'sessionA').sort((a, b) => b.totalScore - a.totalScore);
    const sessionBSubmissions = submissions.filter(s => s.session_name === 'sessionB').sort((a, b) => b.totalScore - a.totalScore);
    
    // Get session display names from loaded briefs, with fallback to generic names
    const sessionABrief = sessionBriefs.find(b => b.session_name === 'sessionA');
    const sessionBBrief = sessionBriefs.find(b => b.session_name === 'sessionB');
    const sessionAName = sessionABrief?.session_display_name || 'Session A';
    const sessionBName = sessionBBrief?.session_display_name || 'Session B';
    
    return (
      <div className="section" style={{ marginBottom: '2rem' }}>
        <div className="section-header">
          <h2 style={{ color: '#ffffff' }}>Game Summary</h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
          <div style={{ padding: '1rem', background: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <div style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '0.5rem' }}>Designers</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ffffff' }}>
              {new Set(submissions.map(s => s.designer_name)).size}
            </div>
          </div>
          <div style={{ padding: '1rem', background: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <div style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '0.5rem' }}>Session A Submissions</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ffffff' }}>
              {sessionASubmissions.length}
            </div>
          </div>
          <div style={{ padding: '1rem', background: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <div style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '0.5rem' }}>Session B Submissions</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ffffff' }}>
              {sessionBSubmissions.length}
            </div>
          </div>
        </div>
  
        {/* Leaderboards Side by Side */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
          {/* Session A Leaderboard */}
          {sessionASubmissions.length > 0 && (
            <div>
              <h3 style={{ marginBottom: '0.75rem', color: '#ffffff', fontSize: '1rem', fontWeight: 600 }}>{sessionAName}</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {sessionASubmissions.map((submission, index) => (
                  <div 
                    key={submission.id}
                    style={{
                      padding: '0.75rem',
                      background: index === 0 ? 'rgba(215, 2, 246, 0.15)' : 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '6px',
                      border: index === 0 ? '2px solid rgba(215, 2, 246, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: 0 }}>
                      <span style={{
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        color: index === 0 ? '#D702F6' : 'rgba(255, 255, 255, 0.6)',
                        minWidth: '24px',
                        flexShrink: 0
                      }}>
                        #{index + 1}
                      </span>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <strong style={{ color: '#ffffff', fontSize: '0.9rem', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{submission.designer_name}</strong>
                        <div style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.6)', marginTop: '0.15rem' }}>
                          {Object.values(submission.scores || {}).filter(s => s > 0).length} reqs
                        </div>
                      </div>
                    </div>
                    <div style={{
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      color: index === 0 ? '#D702F6' : '#ffffff',
                      flexShrink: 0,
                      marginLeft: '0.5rem'
                    }}>
                      {submission.totalScore} pts
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Session B Leaderboard */}
          {sessionBSubmissions.length > 0 && (
            <div>
              <h3 style={{ marginBottom: '0.75rem', color: '#ffffff', fontSize: '1rem', fontWeight: 600 }}>{sessionBName}</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {sessionBSubmissions.map((submission, index) => (
                  <div 
                    key={submission.id}
                    style={{
                      padding: '0.75rem',
                      background: index === 0 ? 'rgba(215, 2, 246, 0.15)' : 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '6px',
                      border: index === 0 ? '2px solid rgba(215, 2, 246, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: 0 }}>
                      <span style={{
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        color: index === 0 ? '#D702F6' : 'rgba(255, 255, 255, 0.6)',
                        minWidth: '24px',
                        flexShrink: 0
                      }}>
                        #{index + 1}
                      </span>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <strong style={{ color: '#ffffff', fontSize: '0.9rem', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{submission.designer_name}</strong>
                        <div style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.6)', marginTop: '0.15rem' }}>
                          {Object.values(submission.scores || {}).filter(s => s > 0).length} reqs
                        </div>
                      </div>
                    </div>
                    <div style={{
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      color: index === 0 ? '#D702F6' : '#ffffff',
                      flexShrink: 0,
                      marginLeft: '0.5rem'
                    }}>
                      {submission.totalScore} pts
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Helper function to render all submissions and review
  const renderAllSubmissionsAndReview = () => {
    if (submissions.length === 0) return null;

    // Get session display names from loaded briefs, with fallback to generic names
    const sessionABrief = sessionBriefs.find(b => b.session_name === 'sessionA');
    const sessionBBrief = sessionBriefs.find(b => b.session_name === 'sessionB');
    const sessionAName = sessionABrief?.session_display_name || 'Session A';
    const sessionBName = sessionBBrief?.session_display_name || 'Session B';

    // Requirements for scoring (same as ReviewPage)
    const SESSION_A_CLIENT_CHANGES = [
      "The fruit on the cereal bowl should be changed to sliced bananas instead of berries",
      "The main color scheme needs to be cooler tones like blue and white for a more refreshing feel",
      "Add a small icon indicating the cereal is high in fiber",
      "Increase the size of the Gluten Free badge to make it more prominent",
      "Add a tagline under the logo 'Start Your Day Right'",
      "Make the box slightly wider to stand out more on the shelves",
      "Add a small transparent window to show the actual cereal inside",
      "Change the font of the slogan to a handwritten style for a more personal touch",
      { text: "Incorporate a space on the box for a short inspirational breakfast message or quote", isBonus: true, points: 4 }
    ];

    const SESSION_B_CLIENT_CHANGES = [
      "Marketing wants the box to use a dark background with neon green and blue accents so we shift away from the white",
      "Add a row of small platform icons under the logo - windows, mac and linux",
      "Make the version sticker circular and move it to the upper right corner",
      "Include a testimonial quote from an IT admin",
      "Add this slogan 'Unify Your IT. Secure Your World' under the product name",
      "Include a small holographic 'Official Product' sticker across the box",
      "Marketing team request the 'Free 30 Day Trial' badge use a starburst shape instead of a circle",
      { text: "Add a special 'Free 30 Day Trial Included!' callout in classic badge style", isBonus: true, points: 4 }
    ];

    const getClientChangesForSubmission = (submission) => {
      const changes = submission.session_name === 'sessionA' ? SESSION_A_CLIENT_CHANGES : SESSION_B_CLIENT_CHANGES;
      // Convert to format expected by ScoringChecklist (with id property)
      return changes.map((req, index) => {
        if (typeof req === 'string') {
          return {
            id: `client_change_${index}`,
            text: req,
            points: 1,
            isBonus: false
          };
        } else {
          return {
            id: `client_change_${index}`,
            text: req.text,
            points: req.points || 4,
            isBonus: req.isBonus || false
          };
        }
      });
    };

    const handleScoreUpdate = async (submissionId, scores) => {
      try {
        const requirementIds = Object.keys(scores);
        
        for (const requirementId of requirementIds) {
          const score = scores[requirementId];
          
          const { data: existing } = await supabase
            .from('scores')
            .select('id')
            .eq('submission_id', submissionId)
            .eq('requirement_id', requirementId);

          const existingScore = existing && existing.length > 0 ? existing[0] : null;

          if (existingScore) {
            await supabase
              .from('scores')
              .update({ score: parseInt(score) || 0 })
              .eq('id', existingScore.id);
          } else {
            await supabase
              .from('scores')
              .insert([{
                submission_id: submissionId,
                requirement_id: requirementId,
                score: parseInt(score) || 0
              }]);
          }
        }
        
        // Reload submissions to update scores
        await loadSubmissions();
      } catch (error) {
        console.error('Error updating scores:', error);
      }
    };

    // Filter submissions by designer and session
    const filteredSubmissions = selectedDesignerFilter === 'All' 
      ? submissions 
      : submissions.filter(s => 
          s.designer_name === selectedDesignerFilter && 
          s.session_name === selectedSessionFilter
        );

    // Get unique designers for filter tabs
    const uniqueDesigners = ['All', ...new Set(submissions.map(s => s.designer_name))];

    // Calculate total scores per designer
    const designerTotals = {};
    submissions.forEach(submission => {
      if (!designerTotals[submission.designer_name]) {
        designerTotals[submission.designer_name] = {
          totalScore: 0,
          submissionCount: 0,
          sessionA: 0,
          sessionB: 0
        };
      }
      designerTotals[submission.designer_name].totalScore += submission.totalScore || 0;
      designerTotals[submission.designer_name].submissionCount += 1;
      if (submission.session_name === 'sessionA') {
        designerTotals[submission.designer_name].sessionA += submission.totalScore || 0;
      } else {
        designerTotals[submission.designer_name].sessionB += submission.totalScore || 0;
      }
    });

    // Sort designers by total score
    const sortedDesigners = Object.entries(designerTotals)
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.totalScore - a.totalScore);

    return (
      <>
        <div className="section" style={{ marginBottom: '2rem' }}>
          <div className="section-header" style={{ marginBottom: '1.5rem' }}>
            <h2>Review & Score Submissions</h2>
          </div>

          {/* Designer Tabs - Segmented Button Style */}
          <div style={{ 
            display: 'inline-flex', 
            background: 'rgba(255, 255, 255, 0.08)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: '8px',
            padding: '2px',
            gap: 0,
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
            marginBottom: '1.5rem',
            flexWrap: 'wrap'
          }}>
            {uniqueDesigners.map((designer, index) => (
              <React.Fragment key={designer}>
                {index > 0 && (
                  <div style={{
                    width: '1px',
                    backgroundColor: 'rgba(255, 255, 255, 0.12)',
                    margin: '4px 0'
                  }} />
                )}
                <button
                  onClick={() => setSelectedDesignerFilter(designer)}
                  style={{
                    padding: '0.5rem 1rem',
                    borderRadius: index === 0 
                      ? (selectedDesignerFilter === designer ? '6px' : '6px 0 0 6px')
                      : index === uniqueDesigners.length - 1
                      ? (selectedDesignerFilter === designer ? '6px' : '0 6px 6px 0')
                      : (selectedDesignerFilter === designer ? '6px' : '0'),
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    transition: 'all 0.2s cubic-bezier(0.2, 0, 0, 1)',
                    backgroundColor: selectedDesignerFilter === designer ? '#ffffff' : 'transparent',
                    color: selectedDesignerFilter === designer ? '#D702F6' : 'rgba(0, 0, 0, 0.6)',
                    boxShadow: selectedDesignerFilter === designer ? '0 1px 2px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)' : 'none',
                    minWidth: '80px',
                    position: 'relative',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedDesignerFilter !== designer) {
                      e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedDesignerFilter !== designer) {
                      e.target.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {designer}
                </button>
              </React.Fragment>
            ))}
          </div>

          {filteredSubmissions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#999' }}>
              <p>
                {submissions.length === 0 
                  ? 'No submissions yet.'
                  : selectedDesignerFilter === 'All'
                    ? 'No submissions found.'
                    : `No submissions from ${selectedDesignerFilter} for ${selectedSessionFilter === 'sessionA' ? sessionAName : sessionBName}.`
                }
              </p>
            </div>
          ) : selectedDesignerFilter === 'All' ? (
            // Show image grid for "All" tab
            <div className="submissions-image-grid">
              {filteredSubmissions.map(submission => (
                <div key={submission.id} className="submission-image-card">
                  <div className="submission-image-header">
                    <span className="submission-designer-name">
                      {submission.designer_name} - {submission.session_name === 'sessionA' ? sessionAName : sessionBName}
                    </span>
                  </div>
                  <div className="submission-image-wrapper">
                    <img 
                      src={submission.image_url} 
                      alt={`${submission.designer_name}'s submission`}
                      className="submission-image"
                    />
                  </div>
                  <div style={{ padding: '0.75rem', fontSize: '0.9rem', color: '#D702F6', fontWeight: 'bold' }}>
                    Score: {submission.totalScore} pts
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Show scoring checklist for individual designer with side-by-side layout
            <>
              <div className="review-submissions-list">
                {filteredSubmissions.map(submission => (
                  <div key={submission.id} className="review-submission-card review-submission-side-by-side">
                    <div className="review-submission-header">
                      <div style={{ paddingLeft: '1.5rem', display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
                        <div>
                          <h3 style={{ margin: 0 }}>
                            {submission.designer_name}
                          </h3>
                        </div>
                        <div style={{ 
                          display: 'inline-flex', 
                          background: 'rgba(255, 255, 255, 0.08)',
                          border: '1px solid rgba(255, 255, 255, 0.12)',
                          borderRadius: '8px',
                          padding: '2px',
                          gap: 0,
                          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
                        }}>
                          <button
                            onClick={() => setSelectedSessionFilter('sessionA')}
                            style={{
                              padding: '0.5rem 1rem',
                              borderRadius: selectedSessionFilter === 'sessionA' ? '6px' : '6px 0 0 6px',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              transition: 'all 0.2s cubic-bezier(0.2, 0, 0, 1)',
                              backgroundColor: selectedSessionFilter === 'sessionA' ? '#ffffff' : 'transparent',
                              color: selectedSessionFilter === 'sessionA' ? '#D702F6' : 'rgba(0, 0, 0, 0.6)',
                              boxShadow: selectedSessionFilter === 'sessionA' ? '0 1px 2px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)' : 'none',
                              minWidth: '120px',
                              position: 'relative'
                            }}
                            onMouseEnter={(e) => {
                              if (selectedSessionFilter !== 'sessionA') {
                                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (selectedSessionFilter !== 'sessionA') {
                                e.target.style.backgroundColor = 'transparent';
                              }
                            }}
                          >
                            {sessionAName}
                          </button>
                          <div style={{
                            width: '1px',
                            backgroundColor: 'rgba(255, 255, 255, 0.12)',
                            margin: '4px 0'
                          }} />
                          <button
                            onClick={() => setSelectedSessionFilter('sessionB')}
                            style={{
                              padding: '0.5rem 1rem',
                              borderRadius: selectedSessionFilter === 'sessionB' ? '6px' : '0 6px 6px 0',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              transition: 'all 0.2s cubic-bezier(0.2, 0, 0, 1)',
                              backgroundColor: selectedSessionFilter === 'sessionB' ? '#ffffff' : 'transparent',
                              color: selectedSessionFilter === 'sessionB' ? '#D702F6' : 'rgba(0, 0, 0, 0.6)',
                              boxShadow: selectedSessionFilter === 'sessionB' ? '0 1px 2px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)' : 'none',
                              minWidth: '120px',
                              position: 'relative'
                            }}
                            onMouseEnter={(e) => {
                              if (selectedSessionFilter !== 'sessionB') {
                                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (selectedSessionFilter !== 'sessionB') {
                                e.target.style.backgroundColor = 'transparent';
                              }
                            }}
                          >
                            {sessionBName}
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="review-submission-content">
                      {/* Full-size image on the left */}
                      <div className="review-submission-image-container">
                        <img 
                          src={submission.image_url} 
                          alt={`${submission.designer_name}'s submission`}
                          className="review-submission-full-image"
                        />
                      </div>
                      {/* Requirements checklist on the right */}
                      <div className="review-submission-checklist-container">
                        <ScoringChecklist
                          clientChanges={getClientChangesForSubmission(submission)}
                          submission={{
                            id: submission.id,
                            designerName: submission.designer_name,
                            sessionName: submission.session_name,
                            scores: submission.scores || {},
                            preview: submission.image_url,
                            imageUrl: submission.image_url
                          }}
                          onScoreUpdate={handleScoreUpdate}
                          hideImage={true}
                          isHistoricalGame={true}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </>
    );
  };

  // Helper function to get logo text based on current view
  const getLogoText = () => {
    // Return 'Creative Coaster' for all views
    if (currentView === 'history') {
      return 'Game History';
    } else if (currentView === 'my-games') {
      return 'My Games';
    } else if (currentView === 'leaderboard') {
      return 'Designer Leaderboard';
    } else if (viewingHistoricalGame && currentGame) {
      // Format game name for display
      const dateToFormat = currentGame.game_date || currentGame.created_at;
      if (dateToFormat) {
        try {
          const date = new Date(dateToFormat);
          if (!isNaN(date.getTime())) {
            const formattedDate = date.toLocaleDateString('en-US', { 
              month: 'short', 
              day: 'numeric', 
              year: 'numeric'
            });
            return `Game - ${formattedDate}`;
          }
        } catch (error) {
          console.error('Error formatting date:', error);
        }
      }
      return currentGame.game_name || 'Game Details';
    }
    return 'The Creative Coaster';
  };

  // If user exists but no profile, create a temporary one
  if (user && !userProfile) {
    // Don't block the app - let them proceed and profile will be created
    console.log('User exists but profile not loaded yet, proceeding anyway');
  }

  return (
    <div className="app">
      {/* Fixed blur overlay at top of page - blurs content scrolling behind header */}
      <div className="header-blur-overlay" />
      {currentView === 'main' && (
        <CardNav
          logo="/rollerCoaster_logo.svg"
          logoAlt="Client Roller Coaster Logo"
          items={navItems}
          baseColor="#242323"
          menuColor="#fff"
          buttonBgColor="#D702F6"
          buttonTextColor="#fff"
          buttonText={isHost() ? 'Host A Game' : 'Start Game'}
          buttonVariant="hero"
          ease="power3.out"
          showButton={showHeaderButton}
          user={user}
          userProfile={userProfile}
          onSignOut={signOut}
          onProfileClick={() => {
            setCurrentView('profile');
            setShowProfile(true);
          }}
          onSignInClick={() => {
            setAuthModalMode('signin');
            setShowAuthModal(true);
          }}
          onButtonClick={() => {
            if (!requireVerifiedHost()) return;
            setCurrentGame(null);
            setShowGameTypeSelectorModal(true);
          }}
        />
      )}
      {/* Use CardNav for Game History, Leaderboard, and Historical Game views */}
      {(currentView === 'history' || currentView === 'my-games' || currentView === 'leaderboard' || viewingHistoricalGame) && (
        <CardNav
          logo="/rollerCoaster_logo.svg"
          logoAlt="Client Roller Coaster Logo"
          items={navItems}
          baseColor="#242323"
          menuColor="#fff"
          buttonBgColor="#D702F6"
          buttonTextColor="#fff"
          ease="power3.out"
          showButton={false}
          showBackButton={true}
          showHamburger={false}
          logoText={getLogoText()}
          user={user}
          userProfile={userProfile}
          onSignOut={signOut}
          onProfileClick={() => {
            setCurrentView('profile');
            setShowProfile(true);
          }}
          onSignInClick={() => {
            setAuthModalMode('signin');
            setShowAuthModal(true);
          }}
                  onBackClick={() => {
                    if (viewingHistoricalGame) {
                      setViewingHistoricalGame(false);
                      setCurrentView('history');
                    } else if (currentView === 'leaderboard' || currentView === 'my-games') {
                      setCurrentView('main');
                    } else {
                      setViewingHistoricalGame(false);
                      setCurrentView('main');
                    }
                  }}
                  backButtonText={viewingHistoricalGame ? '← Back to Game History' : (currentView === 'leaderboard' || currentView === 'my-games') ? '← Back' : '← Back'}
        />
      )}
      
      {/* Use CardNav for Review page */}
      {currentView === 'review' && (
        <>
          <CardNav
            logo="/rollerCoaster_logo.svg"
            logoAlt="Client Roller Coaster Logo"
            items={navItems}
            baseColor="#242323"
            menuColor="#fff"
            buttonBgColor="#D702F6"
            buttonTextColor="#fff"
            ease="power3.out"
            showButton={false}
            showBackButton={true}
            showHamburger={false}
            logoText="Review & Score Submissions"
            user={user}
            userProfile={userProfile}
            onSignOut={signOut}
            onSignInClick={() => {
              setAuthModalMode('signup');
              setShowAuthModal(true);
            }}
            onBackClick={() => {
              if (viewingHistoricalGame) {
                setViewingHistoricalGame(false);
                setCurrentView('history');
              } else {
                    setCurrentView('main');
              }
            }}
            backButtonText="← Back to Game History"
          />
          <main className="app-main" data-view="review" style={{ position: 'relative' }}>
            <ReviewPage currentGameId={currentGame?.id} />
          </main>
        </>
      )}
      
      {/* Option 3: First section exactly one viewport — no peek of content below */}
      {currentView === 'main' && !viewingHistoricalGame && (() => {
        const isGuest = !user?.id;
        // Guests see the same two-column hero as members — profile card + join
        // card on the left, "Teams that play together" card on the right — just
        // populated with sample data instead of a real account.
        const heroName = isGuest
          ? GUEST_DEMO_PROFILE.name
          : (userProfile?.full_name || user.email || 'User');
        const heroAvatarUrl = isGuest ? null : userProfile?.avatar_url;
        const heroIsHost = isGuest ? false : isHost();
        const heroGames = isGuest ? GUEST_DEMO_GAMES : recentGames;
        const heroInitials = (() => {
          const name = isGuest ? GUEST_DEMO_PROFILE.name : (userProfile?.full_name || '');
          const email = isGuest ? '' : (user?.email || '');
          if (name) {
            const parts = name.trim().split(' ');
            return parts.length >= 2
              ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
              : name[0].toUpperCase();
          }
          return email ? email[0].toUpperCase() : '?';
        })();
        // Guests get the sign-in prompt where members open their games list.
        const handleGamesTableClick = isGuest
          ? () => { setAuthModalMode('signin'); setShowAuthModal(true); }
          : () => { setPreviousView('main'); setCurrentView('my-games'); };
        return (
        <div className="first-viewport-wrap">
          <div className="hyperspeed-animation-container">
          {memoizedParticles}
          {memoizedHyperspeed}
          {/* Stretched to the section's content box (App.css) so its height is
              exactly the room the hero has between the header and the bottom of
              the viewport — that measurement drives the fit below. */}
          <div className="hero-fit-area" ref={setHeroFitEl}>
          <div
            className="hero-shell"
            ref={setHeroShellEl}
            style={heroFitTransform < 1 ? {
              transform: `scale(${heroFitTransform})`,
              transformOrigin: 'top center'
            } : undefined}
          >
            <div className="hero-row">
              <div
                className="hero-left-panel"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: heroScaledRem(1, 0.6),
                  width: '100%',
                  minHeight: 0
                }}
              >
                <div
                  className="cta-card returning-card"
                  style={{
                    flex: 1,
                    minHeight: 0,
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(215, 2, 246, 0.35)',
                    borderRadius: '24px',
                    padding: `${heroScaledRem(1.25, 0.85)} ${heroScaledRem(1.5, 1)}`,
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 0 18px rgba(215,2,246,0.3), 0 18px 40px rgba(0,0,0,0.25)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: heroScaledRem(0.75, 0.5)
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: heroScaledRem(1, 0.75) }}>
                    <div style={{
                      width: heroAvatarSize, height: heroAvatarSize, borderRadius: '50%', flexShrink: 0,
                      background: heroAvatarUrl ? 'transparent' : 'linear-gradient(135deg, #D702F6 0%, #8C2BFF 100%)',
                      border: '2px solid rgba(215, 2, 246, 0.6)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      overflow: 'hidden',
                      boxShadow: '0 0 20px rgba(215, 2, 246, 0.3)'
                    }}>
                      {heroAvatarUrl ? (
                        <img
                          src={heroAvatarUrl}
                          alt={heroName}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                        />
                      ) : (
                        <span style={{
                          color: '#fff', fontSize: heroScaledRem(1.5, 1.1), fontWeight: 700,
                          fontFamily: "'Orbitron', sans-serif", lineHeight: 1
                        }}>
                          {heroInitials}
                        </span>
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h3 style={{
                        margin: 0, fontSize: heroScaledRem(1.25, 1), fontWeight: 800, color: 'white',
                        fontFamily: "'Orbitron', sans-serif",
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                      }}>
                        {heroName}
                      </h3>
                      <div style={{ marginTop: '0.35rem' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '0.2rem 0.6rem',
                          borderRadius: '8px',
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          background: heroIsHost ? 'rgba(215, 2, 246, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                          color: heroIsHost ? '#D702F6' : 'rgba(255, 255, 255, 0.85)',
                          border: `1px solid ${heroIsHost ? 'rgba(215, 2, 246, 0.4)' : 'rgba(255, 255, 255, 0.15)'}`
                        }}>
                          {heroIsHost ? 'Host' : 'Player'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <MiniCrtTable
                    games={heroGames}
                    onClick={handleGamesTableClick}
                  />
                </div>

                <div
                  className="cta-card join-card"
                  style={{
                    flex: 1,
                    background: 'rgba(255, 255, 255, 0.06)',
                    border: '1px solid rgba(215, 2, 246, 0.35)',
                    borderRadius: '24px',
                    padding: heroScaledRem(1.5, 1),
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 0 20px rgba(215,2,246,0.35), 0 20px 45px rgba(0,0,0,0.3)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    gap: heroScaledRem(0.9, 0.6)
                  }}
                >
                  <div>
                    <h3 style={{ margin: `0 0 ${heroScaledRem(0.2, 0.15)} 0`, fontSize: heroScaledRem(1.5, 1.15), fontWeight: 800, color: 'white', fontFamily: "'Orbitron', sans-serif" }}>
                      Join a game
                    </h3>
                    <p style={{ margin: 0, color: 'rgba(255,255,255,0.82)', lineHeight: 1.45, fontSize: heroScaledRem(1, 0.88) }}>
                      Enter the join code provided by your host
                    </p>
                  </div>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      const formData = new FormData(e.target);
                      const code = formData.get('joinCode')?.toUpperCase().trim();
                      if (code && code.length === 6) {
                        setJoinCode(code);
                        setCurrentView('join-game');
                        window.history.pushState({}, '', `/join/${code}`);
                      } else {
                        alert('Please enter a valid 6-character join code');
                      }
                    }}
                    style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: heroScaledRem(0.75, 0.5) }}
                  >
                    <input
                      type="text"
                      name="joinCode"
                      placeholder="_ _ _ _ _ _"
                      maxLength={6}
                      className="join-code-input"
                      value={joinCodeInput}
                      style={{
                        width: '100%',
                        padding: `${heroScaledRem(0.8, 0.45)} 1rem`,
                        fontSize: `${heroJoinInputSize}px`,
                        fontWeight: 700,
                        textAlign: 'center',
                        letterSpacing: '0.28em',
                        fontFamily: 'monospace',
                        color: '#D702F6',
                        background: 'transparent',
                        border: 'none',
                        borderRadius: '10px',
                        textTransform: 'uppercase',
                        textShadow: 'none',
                        boxSizing: 'border-box',
                        outline: 'none'
                      }}
                      onFocus={(e) => {
                        e.target.style.outline = 'none';
                        e.target.style.background = 'transparent';
                      }}
                      onBlur={(e) => {
                        e.target.style.outline = 'none';
                        e.target.style.background = 'transparent';
                      }}
                      onChange={handleJoinCodeChange}
                    />
                    <TextRevealButton
                      type="submit"
                      disabled={joinCodeInput.length !== 6}
                      style={{
                        fontSize: heroScaledRem(1, 0.85),
                        padding: `${heroScaledRem(0.8, 0.5)} 2rem`,
                        minHeight: 'auto'
                      }}
                    >
                      Join Game
                    </TextRevealButton>
                  </form>
                </div>
              </div>

              <div
                className="hero-right-stack"
                style={{
                  position: 'relative',
                  background: 'rgba(0, 0, 0, 0.55)',
                  border: '1px solid rgba(215, 2, 246, 0.35)',
                  borderRadius: '28px',
                  boxShadow: '0 0 24px rgba(215,2,246,0.45), 0 30px 90px rgba(0,0,0,0.45)',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: heroScaledRem(1.25, 0.7),
                  backdropFilter: 'blur(14px)',
                  padding: `0 0 ${heroScaledRem(2, 1.1)} 0`
                }}
              >
                {isGuest ? (
                  /* Same image frame as members, with a straight-on rotating pair of game cards */
                  <div
                    className="hero-guest-cardswap-window hero-image"
                    style={{
                      position: 'relative',
                      width: '100%',
                      height: heroIsTwoColumn
                        ? `${Math.max(140, Math.min(260, Math.round(heroCardWindowBudget)))}px`
                        : 'min(clamp(200px, 24vw, 260px), 32vh)',
                      borderRadius: '20px',
                      overflow: 'hidden',
                      boxShadow: '0 25px 60px rgba(0,0,0,0.35)',
                      isolation: 'isolate'
                    }}
                  >
                    <CardSwap
                      width="100%"
                      height="100%"
                      delay={5500}
                      pauseOnHover
                      skewAmount={0}
                      easing="smooth"
                      mode="crossfade"
                    >
                      <Card className="hero-guest-game-card">
                        <div
                          className="hero-guest-game-card-bg"
                          style={{ backgroundImage: "url('/images/roller-coaster-header.png')" }}
                        />
                        <div className="hero-guest-game-card-content">
                          <h3 className="hero-guest-game-card-title">Creative Coaster</h3>
                          <p className="hero-guest-game-card-tagline">Design challenges with client feedback and leaderboards</p>
                        </div>
                      </Card>
                      <Card className="hero-guest-game-card">
                        <div
                          className="hero-guest-game-card-bg"
                          style={{ backgroundImage: "url('/images/find-image-coaster-bg.png')" }}
                        />
                        <div className="hero-guest-game-card-content">
                          <h3 className="hero-guest-game-card-title">Find the Image</h3>
                          <p className="hero-guest-game-card-tagline">Quick rounds. Find the match and level up.</p>
                        </div>
                      </Card>
                    </CardSwap>
                  </div>
                ) : (
                  <div
                    className="hero-image"
                    style={{
                      position: 'relative',
                      width: '100%',
                      /* Same vertical budget as the guest card stack: on a short
                         window the image gives up height so the card below it
                         still ends above the fold. */
                      height: heroIsTwoColumn
                        ? `${Math.max(140, Math.min(260, Math.round(heroCardWindowBudget)))}px`
                        // Stacked: also bounded by screen height so the cards
                        // below it do not sit far down the page.
                        : 'min(clamp(200px, 24vw, 260px), 32vh)',
                      borderRadius: '20px',
                      overflow: 'hidden',
                      boxShadow: '0 25px 60px rgba(0,0,0,0.35)',
                      background: "url('/images/roller-coaster-header.png') center 25%/cover no-repeat",
                      isolation: 'isolate'
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(180deg, rgba(6,0,16,0.15) 0%, rgba(6,0,16,0.8) 100%)'
                      }}
                    />
                  </div>
                )}
                <div
                  className="hero-right-body"
                  ref={setHeroBodyEl}
                  style={{
                    padding: `${heroScaledRem(1.5, 0.9)} 2rem 0 2rem`,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: heroScaledRem(1.25, 0.7),
                    /* Never shrink: its measured height is what the card window
                       above budgets against, so shrinking would feed back. */
                    flexShrink: 0
                  }}
                >
                  <div>
                    <p
                      className="hero-title"
                      style={{
                        fontSize: `clamp(${heroScaledRem(2.2, 1.45)}, 4.5vw, ${heroScaledRem(3, 1.85)})`,
                        fontWeight: 900,
                        margin: `0 0 ${heroScaledRem(0.75, 0.45)} 0`,
                        textShadow: '0 2px 14px rgba(0, 0, 0, 0.8), 0 0 28px rgba(0, 0, 0, 0.5)',
                        fontFamily: "'Orbitron', sans-serif",
                        lineHeight: 1.2,
                        textAlign: 'center'
                      }}
                    >
                      Teams that play together grow together
                    </p>
                    <p
                      className="hero-subtitle"
                      style={{
                        fontSize: heroScaledRem(1.15, 0.95),
                        fontWeight: 400,
                        margin: 0,
                        textShadow: '0 2px 10px rgba(0, 0, 0, 0.8), 0 0 20px rgba(0, 0, 0, 0.5)',
                        fontFamily: "'Orbitron', sans-serif",
                        lineHeight: heroFitScale < 0.9 ? 1.45 : 1.6,
                        color: 'rgba(255,255,255,0.9)'
                      }}
                    >
                      Rally your crew with hyper fast design challenges. Experiment, improvise and watch friendly rivalries level everyone up.
                    </p>
                  </div>
                  <button
                    className="hero-host-game-btn"
                    onClick={handleStartNewGameClick}
                    style={{
                      fontSize: heroScaledRem(1, 0.85),
                      fontWeight: 700,
                      color: 'white',
                      background: 'linear-gradient(120deg, #D702F6 0%, #8C2BFF 100%)',
                      border: 'none',
                      padding: `${heroScaledRem(0.9, 0.6)} 1.2rem`,
                      borderRadius: '12px',
                      cursor: 'pointer',
                      fontFamily: "'Orbitron', sans-serif",
                      textShadow: 'none',
                      boxShadow: '0 10px 25px rgba(215, 2, 246, 0.45)',
                      transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.65rem',
                      alignSelf: 'flex-end'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 14px 32px rgba(215,2,246,0.6)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 10px 25px rgba(215,2,246,0.45)';
                    }}
                  >
                    {(isGuest || heroIsHost) ? 'Host A Game' : 'Become a host'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          </div>
          </div>
        </div>
      );
      })()}
      
      {/* Down arrow button to scroll to MagicBento section - fixed at bottom of viewport */}
      {currentView === 'main' && !viewingHistoricalGame && (
        <div style={{
          position: 'fixed',
          bottom: '30px', // Position at bottom of viewport with margin from edge
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 100, // Higher z-index to ensure it's visible above other content
          pointerEvents: 'none', // Wrapper doesn't capture events, button does
          width: '100%',
          opacity: showScrollIcon ? 1 : 0,
          transition: 'opacity 0.4s ease-out',
          visibility: showScrollIcon ? 'visible' : 'hidden'
        }}>
          <button
            className="more-games-btn"
            onClick={() => {
              const section = document.getElementById('magic-bento-section');
              if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            style={{
              pointerEvents: showScrollIcon ? 'auto' : 'none'
            }}
            aria-label="See Games"
          >
            <svg 
              className="more-games-btn__icon"
              width="18" 
              height="18" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="#D702F6" 
              strokeWidth="2.5" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <path d="M6 9l6 6 6-6"/>
            </svg>
            <span className="more-games-btn__text">See Games</span>
          </button>
        </div>
      )}
      
      {/* MagicBento section - below first viewport (Option 3) */}
      {currentView === 'main' && !viewingHistoricalGame && (
        <section 
          id="magic-bento-section"
          className="magic-bento-section"
          style={{ 
            position: 'relative', 
            padding: '4rem 0',
            paddingTop: '2rem',
            background: '#060010',
            backgroundColor: '#060010',
            minHeight: '100vh',
            width: '100%',
            boxSizing: 'border-box',
            scrollMarginTop: '80px'
          }}
        >
          <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 var(--app-main-padding, 2rem)' }}>
            <h2 style={{
              fontSize: 'clamp(2rem, 5vw, 3.5rem)',
              fontWeight: 700,
              fontFamily: "'Orbitron', sans-serif",
              color: '#ffffff',
              textAlign: 'left',
              margin: '0 0 2.5rem',
              padding: '1rem 0 0',
              letterSpacing: '-0.02em',
              lineHeight: 1.2
            }}>
              Creative Coaster
            </h2>
            {memoizedMagicBento}
          </div>
        </section>
      )}

      {/* Find the Image Showcase section */}
      {currentView === 'main' && !viewingHistoricalGame && (
        <section 
          style={{ 
            position: 'relative', 
            padding: '2rem 0 4rem',
            background: '#060010',
            width: '100%',
            boxSizing: 'border-box'
          }}
        >
          <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 var(--app-main-padding, 2rem)' }}>
            <h2 style={{
              fontSize: 'clamp(2rem, 5vw, 3.5rem)',
              fontWeight: 700,
              fontFamily: "'Orbitron', sans-serif",
              color: '#ffffff',
              textAlign: 'left',
              margin: '0 0 2.5rem',
              padding: '1rem 0 0',
              letterSpacing: '-0.02em',
              lineHeight: 1.2
            }}>
              Find The Image
            </h2>
            {memoizedFindImageShowcase}
          </div>
        </section>
      )}

      {/* Footer */}
      {currentView === 'main' && !viewingHistoricalGame && (
        <Footer />
      )}

      {/* Historical Game View */}
      {currentView === 'main' && viewingHistoricalGame && currentGame && (
        <main className="app-main" data-view="historical-game" style={{ position: 'relative', background: '#060010', minHeight: '100vh', padding: '2rem', paddingTop: 'calc(2em + 60px + 4rem)' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            {/* Game Summary */}
            {renderGameSummary()}

              {/* Game Details - Individual Cards */}
              {(gameDetails.instructions || gameDetails.timing || gameDetails.scoringRules || gameDetails.description) && (
                <div className="section" style={{ marginBottom: '2rem' }}>
                  <div className="section-header">
                    <h2 style={{ color: '#ffffff' }}>Game Details</h2>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1.5rem' }}>
                    {gameDetails.instructions && (
                      <div style={{ 
                        background: 'rgba(255, 255, 255, 0.05)', 
                        borderRadius: '8px', 
                        padding: '1.5rem', 
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        display: 'flex',
                        flexDirection: 'column'
                      }}>
                        <h3 style={{ marginBottom: '0.75rem', color: '#ffffff', fontSize: '1.1rem', fontWeight: 600 }}>Instructions</h3>
                        <p style={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: '1.6', margin: 0, flex: 1 }}>{gameDetails.instructions}</p>
                      </div>
                    )}
                    {gameDetails.timing && (
                      <div style={{ 
                        background: 'rgba(255, 255, 255, 0.05)', 
                        borderRadius: '8px', 
                        padding: '1.5rem', 
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        display: 'flex',
                        flexDirection: 'column'
                      }}>
                        <h3 style={{ marginBottom: '0.75rem', color: '#ffffff', fontSize: '1.1rem', fontWeight: 600 }}>Timing</h3>
                        <p style={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: '1.6', margin: 0, flex: 1 }}>{gameDetails.timing}</p>
                      </div>
                    )}
                    {gameDetails.scoringRules && (
                      <div style={{ 
                        background: 'rgba(255, 255, 255, 0.05)', 
                        borderRadius: '8px', 
                        padding: '1.5rem', 
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        display: 'flex',
                        flexDirection: 'column'
                      }}>
                        <h3 style={{ marginBottom: '0.75rem', color: '#ffffff', fontSize: '1.1rem', fontWeight: 600 }}>Scoring Rules</h3>
                        <p style={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: '1.6', whiteSpace: 'pre-wrap', margin: 0, flex: 1 }}>{gameDetails.scoringRules}</p>
                      </div>
                    )}
                    {gameDetails.description && (
                      <div style={{ 
                        background: 'rgba(255, 255, 255, 0.05)', 
                        borderRadius: '8px', 
                        padding: '1.5rem', 
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        display: 'flex',
                        flexDirection: 'column'
                      }}>
                        <h3 style={{ marginBottom: '0.75rem', color: '#ffffff', fontSize: '1.1rem', fontWeight: 600 }}>Description</h3>
                        <p style={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: '1.6', whiteSpace: 'pre-wrap', margin: 0, flex: 1 }}>{gameDetails.description}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Resources */}
              {resources.length > 0 && (
                <div className="section" style={{ marginBottom: '2rem' }}>
                  <div className="section-header">
                    <h2 style={{ color: '#ffffff' }}>Resources ({resources.length})</h2>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
                    {resources.map(resource => (
                      <div key={resource.id} style={{ background: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px', overflow: 'hidden', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        {resource.imageUrl && (
                          <div style={{ width: '100%', height: '200px', overflow: 'hidden', background: 'rgba(255, 255, 255, 0.02)' }}>
                            <img 
                              src={resource.imageUrl} 
                              alt={resource.name}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          </div>
                        )}
                        <div style={{ padding: '1rem' }}>
                          <h3 style={{ marginBottom: '0.5rem', color: '#ffffff' }}>{resource.name}</h3>
                          {resource.type === 'link' && resource.url && (
                            <a 
                              href={resource.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ color: '#D702F6', textDecoration: 'none' }}
                            >
                              View Resource →
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* All Submissions & Review */}
              {renderAllSubmissionsAndReview()}
          </div>
        </main>
      )}

      {/* Game Type Selector - First step when host starts a new game */}
      <GameTypeSelectorModal
        open={showGameTypeSelectorModal}
        onOpenChange={setShowGameTypeSelectorModal}
        onSelect={(gameType) => {
          if (!requireVerifiedHost()) return;
          if (gameType === 'creative-coaster') {
            setShowNewGameModal(true);
          } else if (gameType === 'find-image') {
            setPreviousView(currentView);
            setCurrentView('find-image');
          }
        }}
      />

      {/* New Game Setup Modal */}
      <NewGameSetupModal
        open={showNewGameModal}
        onOpenChange={(open) => {
          console.log('Modal onOpenChange called with:', open);
          setShowNewGameModal(open);
          // Clear currentGame when closing modal unless it's a draft being edited
          if (!open && currentGame && currentGame.game_status !== 'draft') {
            setCurrentGame(null);
          }
        }}
        onSave={async (details) => {
          try {
            console.log('Saving new game with details:', details);
            setViewingHistoricalGame(false);
            // Keep modal open - don't close yet, let user see "Creating game..." button
            
            // Create game and save ALL data BEFORE navigation
            console.log('Creating game and saving all data...');
            const newGame = await createNewGame(details.gameName, details);
            if (!newGame) {
              // Verification alert already shown by createNewGame; avoid duplicate error
              if (user && !user.email_confirmed_at) return;
              console.error('Game creation returned null');
              alert('Failed to create game. Please try again.');
              // Don't close modal - let user retry
              return;
            }
            
            // Verify critical data was saved before navigating
            console.log('Verifying data was saved...');
            await new Promise(resolve => setTimeout(resolve, 500)); // Small delay to ensure DB writes complete
            
            // Reload game to get latest data including designers
            const { data: reloadedGame, error: reloadError } = await supabase
              .from('games')
              .select('*')
              .eq('id', newGame.id)
              .single();
            
            if (reloadError) {
              console.error('Error reloading game:', reloadError);
            } else if (reloadedGame) {
              console.log('Reloaded game with data:', reloadedGame);
              setCurrentGame(reloadedGame);
            }
            
            // Now close modal and navigate - data should be saved
            setShowNewGameModal(false);
            setShowGameManagement(true);
            setCurrentView('game-management');
          } catch (error) {
            console.error('Error in onSave handler:', error);
            // Keep modal open on error so user can retry
            alert(`Failed to create game: ${error.message || 'Please try again.'}`);
            // Don't close modal - let user see the error and retry
          }
        }}
        currentGame={currentGame && currentGame.game_status === 'draft' ? currentGame : null}
      />

      {currentView === 'history' && (
        <main 
          className="app-main" 
          data-view="history"
          style={{ 
            position: 'relative'
          }}
        >
          <GameHistory 
            onGameSelect={(game) => {
              setCurrentGame(game);
              setViewingHistoricalGame(true);
              setCurrentView('main');
            }}
            onNavigateToReview={(game) => {
              setCurrentGame(game);
              setViewingHistoricalGame(true);
              setCurrentView('review');
            }}
            onStartNewGame={() => {
              if (!requireVerifiedHost()) return;
              setCurrentGame(null);
              setShowGameTypeSelectorModal(true);
            }}
          />
        </main>
      )}

      {currentView === 'leaderboard' && (
        <main 
          className="app-main" 
          data-view="leaderboard"
          style={{ 
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <div style={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            pointerEvents: 'none',
            opacity: 0.5
          }}>
            <Particles
              particleColors={['#D702F6', '#8ec8f8', '#ffffff']}
              particleCount={600}
              particleSpread={20}
              speed={0.08}
              particleBaseSize={80}
              moveParticlesOnHover
              alphaParticles={false}
              disableRotation={false}
              pixelRatio={1}
            />
          </div>
          <div style={{ position: 'relative', zIndex: 1 }}>
            <DesignerLeaderboard />
          </div>
      </main>
      )}

      {currentView === 'my-games' && (
        <main 
          className="app-main" 
          data-view="my-games"
          style={{ 
            position: 'relative'
          }}
        >
          <MyGames 
            onGameSelect={(game) => {
              setCurrentGame(game);
              // Store game ID for persistence
              localStorage.setItem('currentGameId', game.id);
              if (isHost()) {
                // Hosts go to game management page
                setPreviousView('my-games'); // Remember we came from my-games
                setShowGameManagement(true);
                setCurrentView('game-management');
              } else {
                // Designers go to active game view
                setCurrentView('active-game');
              }
            }}
            onNavigateToReview={(game) => {
              setCurrentGame(game);
              setViewingHistoricalGame(false);
              setCurrentView('review');
            }}
            onStartNewGame={() => {
              if (!requireVerifiedHost()) return;
              setShowGameTypeSelectorModal(true);
            }}
            onFindImageGameSelect={(fiGame) => {
              setPreviousView('my-games');
              setFindImageSelectedGameId(fiGame?.id ?? null);
              setCurrentView('find-image');
            }}
          />
        </main>
      )}

      {currentView === 'game-management' && currentGame && (
        <div style={{ backgroundColor: '#060010', minHeight: '100vh' }}>
          <ProtectedRoute requireHost={true}>
            <GameManagement 
              game={currentGame}
              onBack={() => {
                // Navigate back to the previous view, or default to 'main'
                if (previousView === 'profile') {
                  setCurrentView('profile');
                  setPreviousView(null); // Reset previous view
                } else {
                  setCurrentView('main');
                }
                setShowGameManagement(false);
              }}
              onStartGame={async () => {
                console.log('🚀 Start Game clicked from GameManagement');
                // Navigate to active game view - the timer will be started from ActiveGameView
                if (currentGame?.id) {
                  localStorage.setItem('currentGameId', currentGame.id);
                }
                setCurrentView('active-game');
              }}
              onNavigateToReview={(game) => {
                setCurrentGame(game);
                setViewingHistoricalGame(false);
                setCurrentView('review');
              }}
              onNavigateToActiveGame={async () => {
                console.log('[PUBLISH] Navigating to ActiveGameView...', { gameId: currentGame?.id });
                
                // Reload game data to ensure we have the latest published status
                if (currentGame?.id) {
                  const { data: updatedGame, error } = await supabase
                    .from('games')
                    .select('*')
                    .eq('id', currentGame.id)
                    .single();
                  
                  if (error) {
                    console.error('[PUBLISH] Error reloading game:', error);
                  } else if (updatedGame) {
                    console.log('[PUBLISH] Reloaded game:', { status: updatedGame.game_status, joinCode: updatedGame.join_code });
                    setCurrentGame(updatedGame);
                  }
                  
                  localStorage.setItem('currentGameId', currentGame.id);
                }
                
                setCurrentView('active-game');
                console.log('[PUBLISH] Navigation complete');
              }}
            />
          </ProtectedRoute>
        </div>
      )}

      {/* Active Game View - Timer and Designers */}
      {/* Don't render ActiveGameView if game is in draft and user is host (should be in game-management instead) */}
      {/* Note: Not wrapped in ProtectedRoute to allow email-only participants */}
      {currentView === 'active-game' && currentGame && !(isHost() && currentGame.game_status === 'draft') && (
        <div style={{ backgroundColor: '#060010', minHeight: '100vh' }}>
          <ActiveGameView 
            game={currentGame}
            onBack={() => {
              // Navigate back to profile (My Games) for hosts, or main for designers
              console.log('[APP] onBack called from ActiveGameView, isHost:', isHost());
              if (isHost()) {
                console.log('[APP] Setting currentView to profile (My Games)');
                setCurrentView('profile');
                setShowProfile(true);
              } else {
                console.log('[APP] Setting currentView to main');
                setCurrentView('main');
                // Clear stored game ID when leaving
                localStorage.removeItem('currentGameId');
              }
            }}
            onNavigateToReview={() => {
              setCurrentView('review');
            }}
          />
        </div>
      )}

      {/* Profile Page */}
      {currentView === 'profile' && (
        <>
          <CardNav
            logo="/rollerCoaster_logo.svg"
            logoAlt="Client Roller Coaster Logo"
            items={navItems}
            baseColor="#242323"
            menuColor="#fff"
            buttonBgColor="#D702F6"
            buttonTextColor="#fff"
            ease="power3.out"
            showButton={false}
            showBackButton={true}
            showHamburger={false}
            logoText="My Profile"
            user={user}
            userProfile={userProfile}
            onSignOut={signOut}
            onProfileClick={() => {
              setCurrentView('profile');
              setShowProfile(true);
            }}
            onSignInClick={() => {
              setAuthModalMode('signup');
              setShowAuthModal(true);
            }}
            onBackClick={() => {
              setCurrentView('main');
              setShowProfile(false);
            }}
            backButtonText="← Back"
          />
          <ProfilePage 
            onGameSelect={(game) => {
              setCurrentGame(game);
              // Store game ID for persistence
              localStorage.setItem('currentGameId', game.id);
              if (isHost()) {
                // Hosts go to game management page
                setPreviousView('profile'); // Remember we came from profile
                setShowGameManagement(true);
                setCurrentView('game-management');
              } else {
                // Designers go to active game view
                setCurrentView('active-game');
              }
            }}
            onNavigateToReview={(game) => {
              setCurrentGame(game);
              setViewingHistoricalGame(false);
              setCurrentView('review');
            }}
            onStartNewGame={() => {
              if (!requireVerifiedHost()) return;
              setShowGameTypeSelectorModal(true);
            }}
          />
        </>
      )}

      {/* Find the Image Mini-Game - same animated Particles background as Leaderboard */}
      {currentView === 'find-image' && (
        <>
          <CardNav
            logo="/rollerCoaster_logo.svg"
            logoAlt="Client Roller Coaster Logo"
            items={navItems}
            baseColor="#242323"
            menuColor="#fff"
            buttonBgColor="#D702F6"
            buttonTextColor="#fff"
            ease="power3.out"
            showButton={false}
            showBackButton={true}
            showHamburger={false}
            logoText="Find the Image"
            user={user}
            userProfile={userProfile}
            onSignOut={signOut}
            onProfileClick={() => {
              setCurrentView('profile');
            }}
            onSignInClick={() => {
              setAuthModalMode('signup');
              setShowAuthModal(true);
            }}
            onBackClick={() => {
              const handled = findImageBackRef.current?.handleBack?.();
              if (!handled) {
                setCurrentView(previousView || 'main');
                setPreviousView(null);
              }
            }}
            backButtonText="← Back"
          />
          <div className="find-image-page-wrap" style={{ position: 'relative', minHeight: '100vh', background: 'transparent' }}>
            <main
              className="app-main app-main--find-image"
              data-view="find-image"
              style={{ position: 'relative', zIndex: 0 }}
            >
              <FindImageGame
                backRef={findImageBackRef}
                initialJoinCode={findImageJoinCode}
                initialGameId={findImageSelectedGameId}
                onBack={() => {
                  setCurrentView(previousView || 'main');
                  setPreviousView(null);
                  setFindImageJoinCode(null);
                  setFindImageSelectedGameId(null);
                }}
              />
            </main>
            {/* Particle layer on top (pointer-events: none) so stars appear everywhere including center and right up to the card */}
            <div
              className="find-image-particles-layer"
              style={{
                position: 'fixed',
                inset: 0,
                left: 0,
                right: 0,
                top: 0,
                bottom: 0,
                zIndex: 1,
                pointerEvents: 'none',
                opacity: 0.5
              }}
            >
              <Particles
                particleColors={['#D702F6', '#8ec8f8', '#ffffff']}
                particleCount={600}
                particleSpread={20}
                speed={0.08}
                particleBaseSize={80}
                moveParticlesOnHover
                alphaParticles={false}
                disableRotation={false}
                pixelRatio={1}
              />
            </div>
          </div>
        </>
      )}

      {/* Join Game Page */}
      {currentView === 'join-game' && joinCode && (
        <JoinGamePage
          joinCode={joinCode}
          onJoinSuccess={(game) => {
            console.log('[APP] Join success, navigating to active game:', game);
            setCurrentGame(game);
            localStorage.setItem('currentGameId', game.id);
            setCurrentView('active-game');
          }}
          onFindImageGame={(fiJoinCode) => {
            console.log('[APP] Redirecting to Find Image game with code:', fiJoinCode);
            setFindImageJoinCode(fiJoinCode);
            setPreviousView('main');
            setCurrentView('find-image');
          }}
        />
      )}

      {/* Password reset — must render when recovery link lands (was previously never shown) */}
      {(showResetPassword || passwordRecoveryPending) && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 20000,
            background: 'rgba(0,0,0,0.85)',
            backdropFilter: 'blur(12px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1.5rem',
            overflow: 'auto'
          }}
        >
          <ResetPassword
            onSuccess={() => {
              clearPasswordRecovery?.();
              setShowResetPassword(false);
              window.history.replaceState({}, '', '/');
              setCurrentView('main');
            }}
            onCancel={() => {
              clearPasswordRecovery?.();
              setShowResetPassword(false);
              window.history.replaceState({}, '', '/');
              setCurrentView('main');
            }}
          />
        </div>
      )}

      {/* Auth Modal - Always render, Radix UI controls visibility */}
      <AuthModal 
        open={showAuthModal}
        onOpenChange={setShowAuthModal}
        defaultMode={authModalMode}
      />
    </div>
  );
}

export default App;

